import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format, addMonths, subMonths, addDays, subDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday } from "date-fns";
import { LogOut, Download, ChevronDown } from "lucide-react";
import { api, API, gbp2 } from "@/lib/api";
import ConsentReviewDrawer, { ConsentStatusChip } from "@/components/ConsentReviewDrawer";
import ConsentFormManager from "@/pages/ConsentFormManager";
import AdminConsultationCredits from "@/pages/AdminConsultationCredits";
import AdminTopups from "@/pages/AdminTopups";
import AdminModels from "@/pages/AdminModels";
import AdminHomepage from "@/pages/AdminHomepage";
import AdminAftercareForms from "@/pages/AdminAftercareForms";
import AdminTerms from "@/pages/AdminTerms";
import AdminLoyalty from "@/pages/AdminLoyalty";
import AdminReferrals from "@/pages/AdminReferrals";
import AdminPromotions from "@/pages/AdminPromotions";
import AdminPromoCodes from "@/pages/AdminPromoCodes";
import AdminStock from "@/pages/AdminStock";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminWipeTestData from "@/pages/AdminWipeTestData";
import AdminBookingsWipe from "@/pages/AdminBookingsWipe";
import AdminFAQs from "@/pages/AdminFAQs";
import AdminManualBooking from "@/pages/AdminManualBooking";
import { BookingPaymentEditor } from "@/components/BookingPaymentEditor";
import AdminBatchRecords from "@/pages/AdminBatchRecords";

/* ─── Admin status-change Undo toast ───────────────────────────────
   Shows a small top-right sonner toast whenever an admin updates a
   booking's status. Includes a Undo button + 15s countdown with a
   progress bar. Clicking Undo calls POST /bookings/:id/undo-status
   which reverts the status AND cancels the deferred automations
   (aftercare email, review email, loyalty stamp, etc.) that would
   otherwise fire after the window expires. */
function UndoToastContent({ t, bookingId, ref: ref_, newStatus, prevStatus, undoUntil, onReverted, onCommitted }) {
  const endMs = new Date(undoUntil).getTime();
  const [remaining, setRemaining] = useState(Math.max(0, Math.ceil((endMs - Date.now()) / 1000)));
  const firedRef = useRef(false);
  useEffect(() => {
    const iv = setInterval(() => {
      const r = Math.max(0, Math.ceil((endMs - Date.now()) / 1000));
      setRemaining(r);
      if (r <= 0 && !firedRef.current) {
        firedRef.current = true;
        clearInterval(iv);
        // The backend will commit via the deferred task; tell the parent
        // so the row paints the committed status.
        onCommitted && onCommitted();
        toast.dismiss(t);
      }
    }, 250);
    return () => clearInterval(iv);
  }, [t, endMs, onCommitted]);
  const pct = Math.max(0, Math.min(100, (remaining / 60) * 100));
  const doUndo = async () => {
    try {
      await api.post(`/bookings/${bookingId}/undo-status`, {});
      firedRef.current = true;
      toast.dismiss(t);
      toast.success(`Reverted to ${prevStatus}`);
      onReverted && onReverted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not undo");
    }
  };
  return (
    <div data-testid="undo-toast" className="w-[320px] bg-white border hairline border-stone-900/80 shadow-lg">
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="mono-label text-[10px] text-stone-500">{ref_ || "APPOINTMENT"}</p>
          <p className="text-sm text-stone-900 truncate">Marked as <strong>{newStatus}</strong></p>
        </div>
        <button
          data-testid="undo-btn"
          onClick={doUndo}
          className="mono-label text-[11px] px-3 py-2 border border-stone-900 hover:bg-stone-900 hover:text-white transition">
          UNDO <span className="text-stone-500 ml-1">{remaining}s</span>
        </button>
      </div>
      <div className="h-[3px] bg-stone-200" aria-hidden="true">
        <div className="h-full transition-[width] duration-200 ease-linear"
             style={{ width: `${pct}%`, backgroundColor: "#C5A059" }} />
      </div>
    </div>
  );
}

function showUndoToast({ bookingId, ref, newStatus, prevStatus, undoUntil, onReverted, onCommitted }) {
  toast.custom((t) => (
    <UndoToastContent t={t} bookingId={bookingId} ref={ref} newStatus={newStatus}
                      prevStatus={prevStatus} undoUntil={undoUntil}
                      onReverted={onReverted} onCommitted={onCommitted} />
  ), { duration: 62000, position: "top-right" });
}

const NAV = [
  { kind: "tab", label: "Dashboard" },
  { kind: "tab", label: "Calendar" },
  {
    kind: "group",
    label: "Treatments",
    children: ["Treatments", "Promotions", "Promo Codes"],
  },
  { kind: "tab", label: "Reports" },
  { kind: "tab", label: "Analytics" },
  {
    kind: "group",
    label: "Clients",
    children: ["Clients", "Loyalty", "Referrals", "Models", "Top Ups", "Consultation Credits"],
  },
  {
    kind: "group",
    label: "Settings",
    children: ["Consent Forms", "Aftercare Forms", "Homepage", "Settings", "Availability", "Terms", "Stock", "FAQs", "Batch Records", "Wipe Bookings", "Wipe Data"],
  },
];

const ALL_TABS = NAV.flatMap((n) => (n.kind === "tab" ? [n.label] : n.children));

export default function Admin() {
  const [tab, setTab] = useState("Dashboard");
  const [openGroup, setOpenGroup] = useState(null);
  const nav = useNavigate();
  const logout = () => { localStorage.removeItem("clae_token"); nav("/admin/login"); };

  // Keep the parent dropdown open when the user is inside one of its children
  // so the active-page indicator stays visible.
  useEffect(() => {
    const parent = NAV.find((n) => n.kind === "group" && n.children.includes(tab));
    if (parent) setOpenGroup(parent.label);
  }, [tab]);

  // Click-outside → collapse any open dropdown, matching the spec (dropdowns
  // start collapsed and only open on user interaction).
  useEffect(() => {
    const h = (e) => {
      if (!e.target.closest?.("[data-admin-nav]")) setOpenGroup(null);
    };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);

  const activeGroup = (label) =>
    NAV.find((n) => n.kind === "group" && n.label === label)?.children.includes(tab);

  const pickTab = (t) => { setTab(t); setOpenGroup(null); };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="border-b hairline bg-white">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-serif text-2xl text-stone-900">CLAÈ</Link>
            <span className="mono-label">Admin Portal</span>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="mono-label flex items-center gap-2 text-stone-600 hover:text-stone-900"><LogOut size={14} /> Sign out</button>
        </div>
        <nav data-admin-nav className="max-w-7xl mx-auto px-6 flex gap-6 border-t hairline overflow-x-visible relative">
          {NAV.map((n) => {
            if (n.kind === "tab") {
              const active = tab === n.label;
              return (
                <button
                  key={n.label}
                  data-testid={`tab-${n.label.toLowerCase()}`}
                  onClick={() => pickTab(n.label)}
                  className={`py-3 mono-label whitespace-nowrap border-b-2 ${active ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-900"}`}
                >
                  {n.label}
                </button>
              );
            }
            const isOpen = openGroup === n.label;
            const isActive = activeGroup(n.label);
            return (
              <div key={n.label} className="relative">
                <button
                  data-testid={`nav-group-${n.label.toLowerCase()}`}
                  onClick={() => setOpenGroup(isOpen ? null : n.label)}
                  aria-expanded={isOpen}
                  className={`py-3 mono-label whitespace-nowrap border-b-2 inline-flex items-center gap-1 ${
                    isActive ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-900"
                  }`}
                >
                  <span>{n.label.toUpperCase()}</span>
                  <ChevronDown
                    size={12}
                    className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isOpen && (
                  <div
                    data-testid={`nav-group-${n.label.toLowerCase()}-menu`}
                    className="absolute left-0 top-full mt-1 min-w-[220px] bg-white border hairline shadow-lg z-40"
                  >
                    {n.children.map((child) => {
                      const active = tab === child;
                      return (
                        <button
                          key={child}
                          data-testid={`tab-${child.toLowerCase()}`}
                          onClick={() => pickTab(child)}
                          className={`w-full text-left px-4 py-3 mono-label text-sm whitespace-nowrap flex items-center gap-2 border-l-2 ${
                            active
                              ? "border-stone-900 bg-stone-50 text-stone-900"
                              : "border-transparent text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                          }`}
                        >
                          {active && <span className="text-[#C5A059]">•</span>}
                          <span className={active ? "" : "pl-3"}>{child}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-10">
        {tab === "Dashboard" && <Dashboard />}
        {tab === "Calendar" && <CalendarView />}
        {tab === "Clients" && <Clients />}
        {tab === "Loyalty" && <AdminLoyalty />}
        {tab === "Referrals" && <AdminReferrals />}
        {tab === "Treatments" && <Treatments />}
        {tab === "Promotions" && <AdminPromotions />}
        {tab === "Promo Codes" && <AdminPromoCodes />}
        {tab === "Stock" && <AdminStock />}
        {tab === "Wipe Data" && <AdminWipeTestData />}
        {tab === "Wipe Bookings" && <AdminBookingsWipe />}
        {tab === "Batch Records" && <AdminBatchRecords />}
        {tab === "FAQs" && <AdminFAQs />}
        {tab === "Top Ups" && <AdminTopups />}
        {tab === "Consultation Credits" && <AdminConsultationCredits />}
        {tab === "Consent Forms" && <ConsentFormManager />}
        {tab === "Aftercare Forms" && <AdminAftercareForms />}
        {tab === "Terms" && <AdminTerms />}
        {tab === "Availability" && <AvailabilityTab />}
        {tab === "Reports" && <Reports />}
        {tab === "Analytics" && <AdminAnalytics />}
        {tab === "Models" && <AdminModels />}
        {tab === "Homepage" && <AdminHomepage />}
        {tab === "Settings" && <SettingsTab />}
      </main>
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const reload = () => api.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => { reload(); }, []);
  // Auto-refresh every 15s so bookings re-order automatically after a move/status change,
  // without stealing focus while the tab is hidden.
  useEffect(() => {
    const tick = () => { if (!document.hidden) reload(); };
    const iv = setInterval(tick, 15000);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", tick); };
  }, []);
  if (!d) return <p className="mono-label">Loading…</p>;
  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h2 className="font-serif text-4xl font-light">Today</h2>
        <button
          data-testid="manual-book-btn"
          onClick={() => setShowManual(true)}
          className="mono-label text-xs border border-stone-900 bg-stone-900 text-white px-4 py-2 hover:bg-[#C5A059] transition"
          style={{ color: "#fff" }}
        >+ Manually Book Client</button>
      </div>
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Today's Revenue" value={gbp2(d.todays_revenue)} />
        <Stat label="Deposits Collected" value={gbp2(d.deposits_collected)} />
        <Stat label="Outstanding" value={gbp2(d.outstanding_balances)} />
        <Stat label="Appointments" value={d.todays_bookings.length} />
        <Stat label="New Clients" value={d.new_clients_today} />
        <Stat label="Cancellations" value={d.cancellations_today} />
        <Stat label="No-Shows" value={d.no_shows_today} />
        <Stat label="Upcoming" value={d.upcoming.length} />
      </div>
      <h3 className="font-serif text-2xl mt-12 mb-4">Today's Timetable</h3>
      <BookingsTable bookings={d.todays_bookings} onChange={reload} />
      <UpcomingSection bookings={d.upcoming} onChange={reload} />
      <CancelledSection bookings={d.cancelled || []} onChange={reload} />
      <AdminManualBooking open={showManual} onClose={() => setShowManual(false)} onCreated={() => reload()} />
    </div>
  );
}

function CancelledSection({ bookings, onChange }) {
  const [show, setShow] = useState("upcoming");
  const todayIso = format(new Date(), "yyyy-MM-dd");
  const futureCount = bookings.filter((b) => b.date >= todayIso).length;
  const list = show === "all" ? bookings : bookings.filter((b) => b.date >= todayIso);
  return (
    <div data-testid="cancelled-section">
      <div className="mt-12 mb-4 flex items-center justify-between gap-4 flex-wrap">
        <h3 className="font-serif text-2xl" data-testid="cancelled-heading">Cancelled ({list.length})</h3>
        <div className="flex items-center gap-2">
          {[["upcoming", `Future dates (${futureCount})`], ["all", `All cancelled (${bookings.length})`]].map(([k, l]) => (
            <button key={k} data-testid={`cancelled-filter-${k}`} onClick={() => setShow(k)}
              className={`mono-label text-[10px] px-3 py-2 border ${show === k ? "bg-stone-900 text-white border-stone-900" : "hairline hover:border-stone-900"}`}
              style={show === k ? { color: "#fff" } : undefined}>{l}</button>
          ))}
        </div>
      </div>
      {list.length ? <BookingsTable bookings={list} onChange={onChange} /> : (
        <p className="text-sm text-stone-500" data-testid="cancelled-empty">No cancelled bookings.</p>
      )}
    </div>
  );
}

function UpcomingSection({ bookings, onChange }) {
  // "all" = show every upcoming booking. Otherwise we filter by a YYYY-MM key.
  const [monthFilter, setMonthFilter] = useState("all");
  const today = React.useMemo(() => startOfMonth(new Date()), []);
  const [pickerDate, setPickerDate] = useState(today);
  // Precompute how many bookings sit in each month so the picker can show
  // a subtle count next to picked months. Not shown in the picker chrome
  // itself — but consumed by the "clear filter" button label below.
  const monthCounts = React.useMemo(() => {
    const counts = new Map();
    for (const b of bookings || []) {
      if (!b.date) continue;
      const key = b.date.slice(0, 7);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return counts;
  }, [bookings]);
  const filtered = React.useMemo(
    () => monthFilter === "all" ? bookings : (bookings || []).filter((b) => b.date && b.date.startsWith(monthFilter)),
    [bookings, monthFilter]
  );
  const applyPicker = (d) => {
    setPickerDate(startOfMonth(d));
    setMonthFilter(format(d, "yyyy-MM"));
  };
  const clearFilter = () => { setMonthFilter("all"); setPickerDate(today); };
  const pickerLabel = pickerDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  return (
    <div>
      <div className="mt-12 mb-4 flex items-center justify-between gap-4 flex-wrap">
        <h3 className="font-serif text-2xl">Upcoming ({filtered.length})</h3>
        <div className="flex items-center gap-2 flex-wrap" data-testid="upcoming-filter-controls">
          <label className="mono-label text-[10px] text-stone-500">FILTER BY MONTH</label>
          {monthFilter === "all" ? (
            <span className="mono-label text-[10px] text-stone-400">Showing all months</span>
          ) : (
            <span className="mono-label text-[10px] text-stone-700" data-testid="upcoming-active-filter">
              {pickerLabel}{monthCounts.get(monthFilter) ? ` · ${monthCounts.get(monthFilter)} bookings` : " · 0 bookings"}
            </span>
          )}
          <MonthYearPicker value={pickerDate} onChange={applyPicker} min={today} />
          {monthFilter !== "all" && (
            <button
              onClick={clearFilter}
              data-testid="upcoming-clear-filter"
              className="mono-label text-[10px] px-2 py-2 border hairline hover:border-stone-900"
            >Clear</button>
          )}
        </div>
      </div>
      <BookingsTable bookings={filtered} onChange={onChange} />
    </div>
  );
}

function MonthYearPicker({ value, onChange, min }) {
  // Compact "Sep 2026 ▾" jump button used by the calendar + upcoming filter.
  // ``value`` is a JS Date; ``onChange`` receives a new Date at the first of the
  // selected month. ``min`` (optional Date) prevents navigating before it —
  // used for the Upcoming filter which never goes before the current month.
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(value.getFullYear());
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  useEffect(() => { if (open) setYear(value.getFullYear()); }, [open, value]);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (!e.target.closest?.("[data-month-year-picker]")) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, [open]);
  const label = value.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const minYear = min ? min.getFullYear() : 1970;
  const minMonth = min ? min.getMonth() : 0;
  const pick = (m) => {
    onChange(new Date(year, m, 1));
    setOpen(false);
  };
  return (
    <div className="relative" data-month-year-picker>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="month-year-picker-btn"
        aria-expanded={open}
        className="mono-label border hairline bg-white px-3 py-2 text-sm inline-flex items-center gap-2 hover:border-stone-900"
      >
        <span>{label}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="month-year-picker-panel"
          className="absolute z-40 mt-1 right-0 bg-white border hairline shadow-lg p-3 w-[260px]"
        >
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setYear((y) => (min && y - 1 < minYear ? y : y - 1))}
              disabled={min && year - 1 < minYear}
              data-testid="picker-prev-year"
              className="mono-label text-xs px-2 py-1 border hairline disabled:opacity-30"
            >‹</button>
            <span className="font-serif text-xl">{year}</span>
            <button
              onClick={() => setYear((y) => y + 1)}
              data-testid="picker-next-year"
              className="mono-label text-xs px-2 py-1 border hairline"
            >›</button>
          </div>
          <div className="grid grid-cols-4 gap-1">
            {months.map((m, i) => {
              const disabled = min && (year < minYear || (year === minYear && i < minMonth));
              const active = value.getFullYear() === year && value.getMonth() === i;
              return (
                <button
                  key={m}
                  onClick={() => pick(i)}
                  disabled={disabled}
                  data-testid={`picker-month-${i}`}
                  className={`mono-label text-[11px] py-2 border hairline transition ${
                    active ? "bg-stone-900 text-white border-stone-900" :
                    disabled ? "opacity-30 cursor-not-allowed" : "bg-white hover:bg-stone-100"
                  }`}
                >{m}</button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MonthView({ anchor, onOpenDay }) {
  const [rowsByDate, setRowsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const monthStart = React.useMemo(() => startOfMonth(anchor), [anchor]);
  const monthEnd = React.useMemo(() => endOfMonth(anchor), [anchor]);
  const gridStart = React.useMemo(() => startOfWeek(monthStart, { weekStartsOn: 1 }), [monthStart]);
  const gridEnd = React.useMemo(() => endOfWeek(monthEnd, { weekStartsOn: 1 }), [monthEnd]);
  const days = React.useMemo(() => eachDayOfInterval({ start: gridStart, end: gridEnd }), [gridStart, gridEnd]);
  useEffect(() => {
    let dead = false;
    setLoading(true);
    api.get("/bookings", { params: {
      date_from: format(gridStart, "yyyy-MM-dd"),
      date_to:   format(gridEnd,   "yyyy-MM-dd"),
    }}).then((r) => {
      if (dead) return;
      const map = {};
      for (const b of r.data || []) (map[b.date] = map[b.date] || []).push(b);
      // Sort each day by time so the compact list reads chronologically.
      for (const k of Object.keys(map)) map[k].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
      setRowsByDate(map);
    }).catch(() => setRowsByDate({}))
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [gridStart, gridEnd]);

  const dowLabels = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const MAX_INLINE = 3;
  return (
    <div className="border hairline bg-white" data-testid="month-view">
      <div className="grid grid-cols-7 border-b hairline">
        {dowLabels.map((d) => (
          <div key={d} className="mono-label text-[10px] text-stone-500 px-2 py-2 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const list = rowsByDate[key] || [];
          const isOut = !isSameMonth(day, monthStart);
          const today = isToday(day);
          return (
            <button
              type="button"
              key={key}
              onClick={() => onOpenDay(day)}
              data-testid={`month-cell-${key}`}
              className={`text-left border-t border-l hairline min-h-[110px] sm:min-h-[130px] p-2 flex flex-col gap-1 transition-colors hover:bg-stone-50 ${
                isOut ? "bg-stone-50/60 opacity-60" : "bg-white"
              } ${today ? "ring-1 ring-stone-900 ring-inset" : ""}`}
            >
              <div className="flex items-baseline justify-between">
                <span className={`font-serif text-lg ${today ? "text-stone-900" : "text-stone-700"}`}>{format(day, "d")}</span>
                {today && <span className="mono-label text-[9px] text-stone-900" data-testid="month-today-flag">TODAY</span>}
                {!today && list.length > 0 && (
                  <span className="mono-label text-[9px] text-stone-500">{list.length}</span>
                )}
              </div>
              {loading && !isOut ? (
                <span className="text-[10px] text-stone-300">…</span>
              ) : (
                <div className="flex flex-col gap-0.5 overflow-hidden">
                  {list.slice(0, MAX_INLINE).map((b) => {
                    const t = statusTone(b);
                    return (
                      <span
                        key={b.id}
                        title={`${b.time} · ${b.client_name} · ${b.items.map((i)=>i.name).join(", ")}`}
                        className="inline-flex items-center gap-1 text-[10px] leading-tight truncate"
                      >
                        <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${t.dot}`} />
                        <span className={`truncate ${t.text}`}>{b.time} {b.client_name?.split(" ")[0] || ""}</span>
                      </span>
                    );
                  })}
                  {list.length > MAX_INLINE && (
                    <span className="mono-label text-[9px] text-stone-500 mt-0.5">+ {list.length - MAX_INLINE} more</span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CalendarView() {
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [view, setView] = useState("day"); // "day" | "month"
  const [rows, setRows] = useState([]);
  const [slots, setSlots] = useState([]);
  const [fillSlot, setFillSlot] = useState(null); // "HH:MM"
  const isValidDate = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
  const load = async () => {
    // Guard: never fire the API with an empty / malformed date (native
    // ``<input type=date>`` fires an empty-string change when the user
    // presses its × clear icon, which used to trigger a 400).
    if (!isValidDate(date)) return;
    const [r, s] = await Promise.all([
      api.get("/bookings", { params: { date } }),
      api.get("/slots", { params: { date, duration: 30 } }),
    ]);
    setRows(r.data);
    setSlots(s.data.slots || []);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [date]);

  const currentDate = useMemo(() => {
    if (!isValidDate(date)) return new Date();
    const [y, m, d] = date.split("-").map(Number);
    return new Date(y, m - 1, d);
  }, [date]);
  const setFromDate = (d) => setDate(format(d, "yyyy-MM-dd"));
  const nav = (fn) => () => setFromDate(fn(currentDate, 1));
  const jumpMonth = (d) => {
    const dayNum = currentDate.getDate();
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    setFromDate(new Date(d.getFullYear(), d.getMonth(), Math.min(dayNum, last)));
  };
  // Fallback the picker to a safe value when the raw string is empty so the
  // Clear icon never bricks the header.
  const onDateInputChange = (raw) => {
    if (!raw) { setFromDate(new Date()); return; }
    setDate(raw);
  };
  const openDayFromMonth = (jsDate) => {
    setFromDate(jsDate);
    setView("day");
  };

  return (
    <div>
      <div className="flex items-center gap-4 flex-wrap">
        <h2 className="font-serif text-4xl font-light">Calendar</h2>
        <div className="ml-auto flex items-center gap-2 flex-wrap" data-testid="calendar-nav">
          <div className="inline-flex border hairline bg-white" data-testid="calendar-view-switcher">
            {["day","month"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`calendar-view-${v}`}
                aria-pressed={view === v}
                className={`mono-label px-3 py-2 text-[11px] ${
                  view === v ? "bg-stone-900 text-white" : "text-stone-700 hover:bg-stone-100"
                }`}
              >{v.toUpperCase()}</button>
            ))}
          </div>
          <button
            onClick={nav(subMonths)}
            data-testid="cal-prev-month"
            title="Previous month"
            className="mono-label border hairline bg-white px-2 py-2 text-sm hover:border-stone-900"
          >‹‹</button>
          {view === "day" && (
            <button
              onClick={nav(subDays)}
              data-testid="cal-prev-day"
              title="Previous day"
              className="mono-label border hairline bg-white px-2 py-2 text-sm hover:border-stone-900"
            >‹</button>
          )}
          <button
            onClick={() => setFromDate(new Date())}
            data-testid="cal-today"
            className="mono-label border hairline bg-white px-3 py-2 text-sm hover:border-stone-900"
          >Today</button>
          {view === "day" && (
            <button
              onClick={nav(addDays)}
              data-testid="cal-next-day"
              title="Next day"
              className="mono-label border hairline bg-white px-2 py-2 text-sm hover:border-stone-900"
            >›</button>
          )}
          <button
            onClick={nav(addMonths)}
            data-testid="cal-next-month"
            title="Next month"
            className="mono-label border hairline bg-white px-2 py-2 text-sm hover:border-stone-900"
          >››</button>
          <MonthYearPicker value={currentDate} onChange={jumpMonth} />
          {view === "day" && (
            <input
              data-testid="calendar-date"
              type="date"
              value={date}
              onChange={(e) => onDateInputChange(e.target.value)}
              className="border hairline p-2 text-sm"
            />
          )}
        </div>
      </div>
      <p className="mt-2 mono-label text-[10px] text-stone-500" data-testid="calendar-current-date">
        {view === "month"
          ? currentDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" }).toUpperCase()
          : currentDate.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px]" data-testid="calendar-status-legend">
        <span className="mono-label text-stone-400">STATUS KEY</span>
        <LegendDot color="bg-emerald-500" label="Completed" />
        <LegendDot color="bg-yellow-400" label="Confirmed" />
        <LegendDot color="bg-amber-500" label="Pending" />
        <LegendDot color="bg-stone-400" label="No-show" />
        <LegendDot color="bg-red-500" label="Cancelled / Late" />
        {view === "day" && <LegendDot color="bg-white border border-stone-300" label="Available" />}
      </div>
      {view === "month" ? (
        <div className="mt-6"><MonthView anchor={currentDate} onOpenDay={openDayFromMonth} /></div>
      ) : (
        <>
          <div className="mt-6 border hairline bg-white p-4" data-testid="available-slots">
            <p className="mono-label mb-3">Available Slots ({slots.length})</p>
            {slots.length === 0 ? (
              <p className="text-xs text-stone-500">No open slots on this day.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {slots.map((t) => (
                  <button
                    key={t}
                    data-testid={`fill-slot-${t}`}
                    onClick={() => setFillSlot(t)}
                    className="mono-label border hairline px-2.5 py-1.5 hover:bg-stone-900 hover:text-white transition text-[11px]"
                  >
                    {t} · Fill
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="mt-6"><BookingsTable bookings={rows} onChange={load} /></div>
        </>
      )}
      {fillSlot && (
        <FillSlotPicker
          date={date}
          time={fillSlot}
          onClose={() => setFillSlot(null)}
          onDone={() => { setFillSlot(null); load(); }}
        />
      )}
    </div>
  );
}

function MovePicker({ booking, onDone }) {
  const [date, setDate] = useState(booking.date);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(null);
  const duration = booking.duration_min || (booking.items || []).reduce((a, i) => a + (i.duration_min || 0) * (i.quantity || 1), 0) || 30;
  const isValidMoveDate = typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date);
  useEffect(() => {
    if (!isValidMoveDate) return;
    setLoading(true);
    api.get("/slots", { params: { date, duration } })
      .then((r) => setSlots(r.data.slots || []))
      .finally(() => setLoading(false));
  }, [date, duration, isValidMoveDate]);
  const move = async (time) => {
    setMoving(time);
    try {
      const r = await api.post(`/bookings/${booking.id}/reschedule`, { date, time });
      toast.success(`Moved to ${r.data.to.date} · ${r.data.to.time}`);
      onDone({ date, time });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
    setMoving(null);
  };
  return (
    <div className="border hairline bg-alt p-3" data-testid="move-picker">
      <label className="mono-label text-[10px]">Choose a new date</label>
      <input data-testid="move-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full border hairline p-2 text-sm" />
      <p className="mono-label text-[10px] mt-3 mb-2">Available slots ({slots.length})</p>
      {loading ? <p className="text-xs text-stone-500">Loading…</p> : (
        <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
          {slots.map((t) => (
            <button
              key={t}
              data-testid={`move-to-${t}`}
              disabled={moving === t}
              onClick={() => move(t)}
              className="mono-label border hairline bg-white px-3 py-2 hover:bg-stone-900 hover:text-white transition text-xs disabled:opacity-50"
            >
              {moving === t ? "Moving…" : t}
            </button>
          ))}
          {slots.length === 0 && <span className="text-xs text-stone-500">No availability on this day.</span>}
        </div>
      )}
    </div>
  );
}

function FillSlotPicker({ date, time, onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [moving, setMoving] = useState(null);
  useEffect(() => {
    api.get("/movable-bookings").then((r) => setRows(r.data || []));
  }, []);
  const filtered = rows.filter((r) => {
    const s = q.toLowerCase();
    return !s || (r.client_name || "").toLowerCase().includes(s) || (r.client_phone || "").toLowerCase().includes(s) || (r.reference || "").toLowerCase().includes(s);
  });
  const pick = async (b) => {
    setMoving(b.id);
    try {
      await api.post(`/bookings/${b.id}/reschedule`, { date, time });
      toast.success(`${b.client_name} moved to ${date} · ${time}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
    setMoving(null);
  };
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="fill-slot-picker">
      <div className="bg-white w-full max-w-lg border hairline max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b hairline">
          <p className="mono-label text-[10px] text-stone-400">FILL THIS SLOT</p>
          <h3 className="font-serif text-2xl mt-1">{date} · {time}</h3>
          <p className="text-xs text-stone-500 mt-1">Bookings flagged "available earlier" appear first.</p>
        </div>
        <div className="p-5">
          <input
            data-testid="fill-search"
            autoFocus
            placeholder="Search by name, phone, or reference"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full border hairline p-2 text-sm mb-4"
          />
          {filtered.length === 0 ? (
            <p className="text-xs text-stone-500">No movable bookings found.</p>
          ) : (
            <ul className="space-y-2">
              {filtered.map((b) => (
                <li key={b.id} className={`border hairline p-3 flex items-center justify-between gap-3 ${b.available_earlier ? "bg-amber-50 border-amber-300" : "bg-white"}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{b.client_name} {b.available_earlier && <span className="ml-1 mono-label text-[9px] text-amber-800">⏱ EARLIER OK</span>}</p>
                    <p className="text-xs text-stone-500 truncate">{b.date} · {b.time} · {b.items.map((i) => i.name).join(", ")}</p>
                  </div>
                  <button
                    data-testid={`fill-pick-${b.reference}`}
                    disabled={moving === b.id}
                    onClick={() => pick(b)}
                    className="mono-label bg-stone-900 text-white px-3 py-2 text-xs whitespace-nowrap disabled:opacity-40 hover:bg-[#C5A059] transition"
                    style={{ color: "#fff" }}
                  >
                    {moving === b.id ? "Moving…" : "Move Here"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-5 border-t hairline flex justify-end">
          <button onClick={onClose} className="mono-label border hairline px-4 py-2 hover:bg-alt" data-testid="fill-close">Close</button>
        </div>
      </div>
    </div>
  );
}

function ReminderStatusPanel({ booking }) {
  const b = booking || {};
  const fmtLondon = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-GB", {
        timeZone: "Europe/London",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false,
      }).replace(",", " ·");
    } catch (_) { return iso; }
  };
  const isConsentApplicable = !b.is_consultation && !b.is_topup;

  const rows = [
    {
      key: "48h",
      label: "48-hour reminder",
      scheduled: b.reminder_48h_scheduled_at,
      sent: b.reminder_48h_sent,
      sentAt: b.reminder_48h_sent_at,
      skip: b.reminder_48h_skipped_reason,
      err: b.reminder_48h_error,
      applicable: true,
      testid: "reminder-48h-row",
    },
    {
      key: "24h",
      label: "24-hour reminder",
      scheduled: b.reminder_24h_scheduled_at,
      sent: b.reminder_24h_sent,
      sentAt: b.reminder_24h_sent_at,
      skip: b.reminder_24h_skipped_reason,
      err: b.reminder_24h_error,
      applicable: true,
      testid: "reminder-24h-row",
    },
    {
      key: "consent",
      label: "Consent-form reminder (24h)",
      scheduled: b.consent_reminder_scheduled_at,
      sent: b.consent_reminder_sent,
      sentAt: b.consent_reminder_sent_at,
      skip: b.consent_reminder_skipped_reason,
      err: null,
      applicable: isConsentApplicable,
      testid: "reminder-consent-row",
    },
  ];

  const statusChip = (row) => {
    if (!row.applicable) return { label: "N/A", tone: "bg-stone-100 text-stone-500 border-stone-300" };
    if (row.err) return { label: "ERROR", tone: "bg-red-50 text-red-700 border-red-300" };
    if (row.sent && row.sentAt && !row.skip) return { label: "SENT", tone: "bg-emerald-50 text-emerald-800 border-emerald-300" };
    if (row.sent && row.skip) return { label: "SKIPPED", tone: "bg-amber-50 text-amber-800 border-amber-300" };
    if (row.sent && !row.sentAt) return { label: "SKIPPED", tone: "bg-amber-50 text-amber-800 border-amber-300" };
    // Not sent yet
    if (row.scheduled) {
      const due = new Date(row.scheduled).getTime();
      if (Date.now() >= due) return { label: "PENDING (DUE)", tone: "bg-amber-50 text-amber-800 border-amber-300" };
      return { label: "SCHEDULED", tone: "bg-stone-50 text-stone-700 border-stone-300" };
    }
    return { label: "—", tone: "bg-stone-50 text-stone-500 border-stone-300" };
  };

  return (
    <div data-testid="reminder-status-panel">
      <p className="text-[11px] text-stone-500 mb-3 leading-relaxed">
        All reminders are scheduled at <span className="font-medium">appointment&nbsp;time − N&nbsp;hours</span> in Europe/London
        and dispatched by the hourly cron. If a cron tick is skipped, the next tick catches up (24h reminder up to 12h late, 48h up to 24h late).
      </p>
      <ul className="space-y-3 text-xs">
        {rows.map((r) => {
          const chip = statusChip(r);
          return (
            <li key={r.key} data-testid={r.testid} className="border hairline p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-stone-900">{r.label}</span>
                <span className={`mono-label inline-block px-2 py-1 border text-[10px] ${chip.tone}`}>{chip.label}</span>
              </div>
              {!r.applicable ? (
                <p className="text-[11px] text-stone-500">
                  Not applicable — {b.is_topup ? "top-up appointments reuse the original consent." : "consultations do not require a treatment consent form."}
                </p>
              ) : (
                <div className="space-y-1 text-stone-600">
                  <div className="flex justify-between gap-3">
                    <span className="text-stone-500">Scheduled for</span>
                    <span className="text-stone-900 text-right" data-testid={`${r.testid}-scheduled`}>
                      {fmtLondon(r.scheduled)}{r.scheduled && <span className="text-stone-400 text-[10px]"> Europe/London</span>}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-stone-500">Sent at</span>
                    <span className="text-stone-900 text-right" data-testid={`${r.testid}-sent-at`}>
                      {r.sentAt ? fmtLondon(r.sentAt) : "—"}
                    </span>
                  </div>
                  {r.skip && (
                    <div className="mt-1 text-amber-800 text-[11px]" data-testid={`${r.testid}-skip`}>
                      Skipped: {r.skip}
                    </div>
                  )}
                  {r.err && (
                    <div className="mt-1 text-red-700 text-[11px]" data-testid={`${r.testid}-error`}>
                      Error: {r.err}
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ManualConsentReminder({ booking, onSent }) {
  const [alt, setAlt] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState(booking.consent_reminder_history || []);
  React.useEffect(() => { setHistory(booking.consent_reminder_history || []); }, [booking]);
  const validEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
  const altOk = !alt.trim() || validEmail(alt);
  const send = async () => {
    if (alt.trim() && !validEmail(alt)) { toast.error("Please enter a valid email address"); return; }
    setSending(true);
    try {
      const r = await api.post(`/bookings/${booking.id}/consent-reminder`,
        { alternative_email: alt.trim() || null });
      if (r.data.delivered) {
        toast.success(r.data.used_alternative_email
          ? `Consent reminder sent to ${r.data.delivered_to} (client's primary email unchanged)`
          : `Consent reminder sent to ${r.data.delivered_to}`);
      } else {
        toast.info("Client has already submitted consent — no reminder sent.");
      }
      setHistory((prev) => [...prev, r.data.history_entry]);
      setAlt("");
      onSent && onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminder");
    }
    setSending(false);
  };
  return (
    <div data-testid="manual-consent-reminder">
      <p className="text-xs text-stone-500 mb-3">Client's primary email: <span className="font-medium text-stone-800">{booking.client_email}</span></p>
      <label className="block mono-label text-[10px] text-stone-500 mb-1">Send to alternative email (optional)</label>
      <input
        data-testid="alternative-email-input"
        type="email"
        value={alt}
        onChange={(e) => setAlt(e.target.value)}
        placeholder="e.g. work@email.com"
        className={`w-full border hairline bg-white p-2 text-sm focus:outline-none focus:border-stone-900 ${alt && !altOk ? "border-red-400" : ""}`}
      />
      {alt && !altOk && <p className="text-xs text-red-600 mt-1">Invalid email format</p>}
      <p className="text-[11px] text-stone-500 mt-2 leading-relaxed">Leave blank to send to the client's primary email. Using an alternative email does not overwrite the client's account email.</p>
      <button
        data-testid="send-consent-reminder"
        onClick={send}
        disabled={sending || (alt && !altOk)}
        className="mt-3 border border-stone-900 px-4 py-2 mono-label hover:bg-stone-900 hover:text-white transition disabled:opacity-40"
      >{sending ? "Sending…" : "Send Consent Reminder"}</button>
      {history.length > 0 && (
        <div className="mt-4">
          <p className="mono-label text-[10px] text-stone-500 mb-2">Reminder history ({history.length})</p>
          <ul className="space-y-2 text-xs">
            {history.slice().reverse().map((h) => (
              <li key={h.id || h.sent_at} className="border-l-2 border-stone-300 pl-3" data-testid={`reminder-history-${h.id || h.sent_at}`}>
                <div className="text-stone-900">
                  {h.trigger === "manual" ? "Manual" : "Automatic"} · {(h.sent_at || "").slice(0, 16).replace("T", " ")} UTC
                  {" · "}<span className={h.delivered ? "text-emerald-700" : "text-amber-700"}>{h.delivered ? "SENT" : "SKIPPED"}</span>
                </div>
                <div className="text-stone-600">
                  To <span className="font-medium">{h.delivered_to}</span>
                  {h.alternative_email && <span className="text-[10px] text-[#8A6D2A] ml-2">(alternative)</span>}
                </div>
                {h.sent_by && <div className="text-stone-500 text-[10px]">by {h.sent_by}</div>}
                {h.skipped_reason && <div className="text-amber-700 text-[10px]">{h.skipped_reason}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BookingsTable({ bookings, onChange }) {
  const [open, setOpen] = useState(null);
  const [rows, setRows] = useState(bookings);
  const [bufferMin, setBufferMin] = useState(15);
  useEffect(() => { api.get("/settings").then((r) => setBufferMin(r.data?.buffer_min ?? 15)).catch(() => {}); }, []);
  // Re-sync local mirror when the parent prop changes (fresh fetch).
  useEffect(() => { setRows(bookings); }, [bookings]);
  const patchLocal = (id, fields) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...fields } : r)));
    // Only sync the drawer if it's still open on this booking — never resurrect a closed drawer.
    setOpen((o) => (o && o.id === id ? { ...o, ...fields } : o));
  };
  // Chronological by time; only No-Show sinks to the bottom (per admin request).
  const rank = (s) => (s === "No-Show" ? 1 : 0);
  const padTime = (t) => {
    if (!t) return "00:00";
    const [h, m] = String(t).split(":");
    return `${String(h).padStart(2, "0")}:${String(m || "00").padStart(2, "0")}`;
  };
  const sortKey = (r) => `${r.date || ""} ${padTime(r.time)}`;
  const orderedRows = React.useMemo(
    () => [...(rows || [])].sort((a, b) => (rank(a.appointment_status) - rank(b.appointment_status)) || sortKey(a).localeCompare(sortKey(b))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows]
  );
  if (!orderedRows || orderedRows.length === 0) return <p className="text-stone-500 text-sm">No appointments.</p>;
  return (
    <div className="border hairline bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-alt mono-label">
          <tr><th className="text-left px-3 py-2">Time</th><th className="text-left px-3 py-2">Client</th><th className="text-left px-3 py-2">Treatments</th><th className="text-right px-3 py-2">Total</th><th className="text-left px-3 py-2">Payment</th><th className="text-left px-3 py-2">Status</th><th></th></tr>
        </thead>
        <tbody>
          {orderedRows.map((b) => (
            <tr key={b.id} className={`border-t hairline ${(b.appointment_status === "Cancelled" || b.appointment_status === "No-Show") ? "bg-stone-50/60" : ""}`} data-testid={`booking-row-${b.reference}`}>
              <td className="px-3 py-2 font-mono align-top">
                {b.date} · {b.time}
                {bufferMin > 0 && !["Cancelled", "No-Show"].includes(b.appointment_status) && (() => {
                  const [hh, mm] = String(b.time || "00:00").split(":").map(Number);
                  const totalEnd = (hh * 60 + mm) + Number(b.duration_min || 0) + bufferMin;
                  const eh = Math.floor((totalEnd % (24 * 60)) / 60);
                  const em = totalEnd % 60;
                  const endStr = `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
                  return (
                    <div className="mt-0.5 text-[10px] font-sans text-amber-700" data-testid={`buffer-chip-${b.reference}`} title="Cleaning + set-up buffer">
                      +{bufferMin}m buffer · ends {endStr}
                    </div>
                  );
                })()}
              </td>
              <td className="px-3 py-2 align-top">{b.client_name}<br /><span className="text-stone-500 text-xs">{b.client_phone}</span></td>
              <td className="px-3 py-2 align-top">
                {b.items.map(i => `${i.name} × ${i.quantity}`).join(", ")}
                {b.items.some((i) => i.product_batch?.batch_number) && (
                  <div className="mt-0.5 text-[11px] text-stone-600" data-testid={`dashboard-batch-${b.reference}`}>
                    {b.items.filter((i) => i.product_batch?.batch_number).map((i, idx) => (
                      <div key={idx}>Batch · {b.items.length > 1 ? `${i.name}: ` : ""}{i.product_batch.batch_number}</div>
                    ))}
                  </div>
                )}
                {b.is_model && (
                  <div className="mt-0.5 text-[10px] mono-label inline-block px-1.5 py-0 border border-[#C5A059]/40 bg-[#FBF6EA] text-[#8A6D2A]" data-testid={`model-day-tag-${b.reference}`}>
                    MODEL DAY
                  </div>
                )}
                {b.is_consultation && (b.items || [])[0]?.consultation_target_label && (
                  <div className="mt-0.5 text-[10px] mono-label inline-block px-1.5 py-0 border border-[#C5A059]/40 bg-[#FBF6EA] text-[#8A6D2A]" data-testid={`consult-target-${b.reference}`}>
                    FOR · {b.items[0].consultation_target_label}
                  </div>
                )}
              </td>
              <td className="px-3 py-2 text-right font-serif text-base align-top">{gbp2(b.subtotal)}</td>
              <td className="px-3 py-2 align-top"><Badge>{b.payment_status}</Badge></td>
              <td className="px-3 py-2 align-top">
                <div className="flex flex-col gap-0.5" data-testid={`status-cell-${b.reference}`}>
                  <StatusPill booking={b} />
                  <ConsentSubchip booking={b} />
                  {b.late_cancel && (
                    <span
                      data-testid={`late-cancel-chip-${b.reference}`}
                      className="inline-flex items-center gap-1 text-[10px] text-red-600 whitespace-nowrap"
                      title="Late cancel — deposit forfeited"
                    >
                      <span aria-hidden="true">⚠</span>
                      <span>Late · deposit forfeited</span>
                    </span>
                  )}
                  {b.referral_status === "INVALIDATED" && (
                    <span
                      data-testid={`referral-invalidated-chip-${b.reference}`}
                      title={`Referring client cancelled${b.referred_client_notified ? " · client notified" : ""}`}
                      className="inline-flex items-center gap-1 text-[10px] text-red-600 whitespace-nowrap"
                    >
                      <span aria-hidden="true">✕</span>
                      <span>Referral invalidated</span>
                    </span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 align-top text-right">
                <button
                  data-testid={`view-booking-${b.reference}`}
                  onClick={() => setOpen(b)}
                  className="mono-label text-[11px] text-stone-600 hover:text-stone-900"
                >View →</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && (
        <BookingModal
          booking={open}
          onClose={() => setOpen(null)}
          onLocalPatch={patchLocal}
          onChange={onChange}
        />
      )}
    </div>
  );
}

function BookingModal({ booking, onClose, onLocalPatch, onChange }) {
  const [amt, setAmt] = useState(booking.remaining_balance);
  const [b, setB] = useState(booking);
  const [movePickerOpen, setMovePickerOpen] = useState(false);
  const [consentForms, setConsentForms] = useState([]);
  const [openConsentId, setOpenConsentId] = useState(null);
  const [showBatchRecord, setShowBatchRecord] = useState(false);
  const loadConsentForms = React.useCallback(() => {
    api.get(`/consent-forms/by-booking/${booking.id}`)
      .then((r) => setConsentForms(r.data))
      .catch(() => setConsentForms([]));
  }, [booking.id]);
  useEffect(() => { setB(booking); loadConsentForms(); }, [booking, loadConsentForms]);
  const update = async (fields) => {
    // Close the drawer FIRST so a stale ref can't resurrect it mid-render.
    onClose && onClose();
    try {
      const resp = await api.patch(`/bookings/${b.id}`, fields);
      // 15-second Undo buffer for appointment_status changes — the
      // backend does NOT finalize the new status until the buffer
      // expires. The row keeps its prior appointment_status until then
      // but gains a ``pending_appointment_status`` field for display.
      if (fields.appointment_status && resp.data?.undo_until && resp.data?.prev_status) {
        onLocalPatch && onLocalPatch(b.id, {
          pending_appointment_status: fields.appointment_status,
          prev_appointment_status: resp.data.prev_status,
          status_undo_until: resp.data.undo_until,
        });
        showUndoToast({
          bookingId: b.id,
          ref: b.reference,
          newStatus: fields.appointment_status,
          prevStatus: resp.data.prev_status,
          undoUntil: resp.data.undo_until,
          onReverted: () => {
            onLocalPatch && onLocalPatch(b.id, {
              pending_appointment_status: null,
              status_undo_until: null,
            });
            onChange && onChange();
          },
          onCommitted: () => {
            onLocalPatch && onLocalPatch(b.id, {
              appointment_status: fields.appointment_status,
              pending_appointment_status: null,
              status_undo_until: null,
            });
            onChange && onChange();
          },
        });
      } else {
        onLocalPatch && onLocalPatch(b.id, fields);
        toast.success("Updated");
      }
      // Background refetch so derived fields (late_cancel, deposit_forfeited) settle.
      if (onChange) {
        const bid = b.id;
        Promise.resolve(onChange()).then(async () => {
          try {
            const r = await api.get(`/bookings/${bid}`);
            onLocalPatch && onLocalPatch(bid, r.data);
          } catch (_) { /* row may have moved off the current view */ }
        });
      }
    } catch (e) {
      toast.error("Update failed");
      // Revert the optimistic row change from server state.
      onLocalPatch && onLocalPatch(booking.id, booking);
    }
  };
  const markPaid = async () => {
    await api.post(`/bookings/${b.id}/mark-paid`, { amount: parseFloat(amt), method: "cash" });
    toast.success("Payment recorded");
    if (onChange) {
      const bid = b.id;
      Promise.resolve(onChange()).then(async () => {
        try { const r = await api.get(`/bookings/${bid}`); setB(r.data); onLocalPatch && onLocalPatch(bid, r.data); } catch (_) {}
      });
    }
  };
  const downloadConsent = async () => {
    const token = localStorage.getItem("clae_token");
    const r = await fetch(`${API}/bookings/${b.id}/consent.pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("No signed consent on this booking"); return; }
    const blob = await r.blob(); const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = `clae-consent-${b.reference}.pdf`; a.click();
    URL.revokeObjectURL(u);
  };
  return (
    <div className="fixed inset-0 z-40 bg-stone-900/30 backdrop-blur-sm" onClick={onClose}>
      <div className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-8">
          <p className="mono-label">Reference · {b.reference}</p>
          <h3 className="font-serif text-3xl mt-2">{b.client_name}</h3>
          <p className="text-sm text-stone-600 mt-1">{b.client_email} · {b.client_phone}</p>
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="modal-current-status">
            <span className="mono-label text-stone-400 text-[10px]">CURRENT STATUS</span>
            <StatusPill booking={b} />
            {b.late_cancel && (
              <span className="inline-flex items-center gap-1 text-[10px] text-red-600 whitespace-nowrap">
                <span aria-hidden="true">⚠</span>
                <span>Late · deposit forfeited</span>
              </span>
            )}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <Info label="Date" value={b.date} /><Info label="Time" value={b.time} />
            <Info label="Duration" value={`${b.duration_min} min`} /><Info label="Total" value={gbp2(b.subtotal)} />
            <Info label="Paid" value={gbp2(b.amount_paid)} /><Info label="Balance" value={gbp2(b.remaining_balance)} />
            <Info label="Booking type" value={b.is_model ? "Model Day" : "Standard"} />
            <Info label="Payment" value={`${b.payment_status || "—"}${b.payment_method ? ` · ${b.payment_method === "cash" ? "Cash" : "Card"}` : ""}`} />
            {b.original_price != null && b.is_model && <Info label="Standard price" value={gbp2(b.original_price)} />}
          </div>
          {b.is_model && (
            <div className="mt-3 text-[10px] mono-label inline-block px-2 py-0.5 border border-[#C5A059]/40 bg-[#FBF6EA] text-[#8A6D2A]" data-testid="modal-model-day-tag">MODEL DAY APPOINTMENT</div>
          )}
          <div className="mt-6 border-t hairline pt-4">
            <p className="mono-label mb-2">Treatments</p>
            {b.items.map((i, idx) => (
              <div key={idx}>
                <div className="flex justify-between py-1 text-sm">
                  <span>{i.name} × {i.quantity}</span>
                  <span>{gbp2(i.price_at_booking * i.quantity)}</span>
                </div>
                {i.product_batch?.batch_number && (
                  <div className="ml-3 -mt-1 mb-2 text-xs text-stone-600" data-testid={`modal-batch-${b.reference}-${idx}`}>
                    Batch · <span className="font-medium">{i.product_batch.batch_number}</span>
                    {i.product_batch.product_name ? ` · ${i.product_batch.product_name}` : ""}
                    {i.product_batch.batch_expiry ? ` · exp ${i.product_batch.batch_expiry}` : ""}
                    {i.product_batch.quantity_used ? ` · ${i.product_batch.quantity_used}` : ""}
                  </div>
                )}
                {i.consultation_target_label && (
                  <div className="ml-3 -mt-1 mb-2 text-xs text-[#8A6D2A]" data-testid={`modal-consult-target-${b.reference}`}>
                    Consultation for · <span className="font-medium">{i.consultation_target_label}</span>
                  </div>
                )}
              </div>
            ))}
            {b.is_consultation && (b.consultation_credits_issued || []).length === 0 && b.payment_status !== "Awaiting Payment" && (
              <div className="mt-3 border hairline p-3 bg-alt text-xs" data-testid="consult-credit-info">
                <p className="mono-label text-[#8A6D2A]">£50 Consultation Credit · Active</p>
                <p className="mt-1 text-stone-600">
                  Auto-applies to this client's next booking of {(b.items || [])[0]?.consultation_target_label || "the selected treatment"}.
                </p>
              </div>
            )}
          </div>
          <div className="mt-4">
            <button
              data-testid={`open-batch-record-${b.reference}`}
              onClick={() => setShowBatchRecord(true)}
              className="w-full mono-label border hairline py-3 hover:bg-alt"
            >
              Add / Edit Product &amp; Batch Record
            </button>
          </div>
          {!b.is_consultation && (
            <AccordionSection title="Send Consent Reminder" testid="send-consent-reminder-accordion">
              <ManualConsentReminder booking={b} onSent={loadConsentForms} />
            </AccordionSection>
          )}
          <AccordionSection
            title="Reminder Status"
            testid="reminder-status-accordion"
            headerRight={<ReminderStatusChip booking={b} />}
          >
            <ReminderStatusPanel booking={b} />
          </AccordionSection>
          {consentForms.length > 0 && (
            <div className="mt-6 border-t hairline pt-4" data-testid="booking-consent-forms">
              <p className="mono-label mb-3">Consent Forms ({consentForms.length})</p>
              <ul className="space-y-3">
                {consentForms.map((cf) => (
                  <li
                    key={cf.treatment_id}
                    className="border hairline p-3 flex items-start justify-between gap-3"
                    data-testid={`booking-cf-${cf.treatment_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-900 truncate">{cf.treatment_name}</p>
                      <div className="mt-2">
                        <ConsentStatusChip
                          status={cf.status}
                          testid={`booking-cf-chip-${cf.treatment_id}`}
                        />
                      </div>
                      {cf.submitted_at && (
                        <p className="mt-2 text-[10px] text-stone-500">
                          Submitted {(cf.submitted_at || "").slice(0, 16).replace("T", " ")} UTC
                          {cf.reviewed_by && ` · Signed by ${cf.reviewed_by}`}
                        </p>
                      )}
                    </div>
                    {cf.form_id ? (
                      <button
                        data-testid={`view-consent-${cf.treatment_id}`}
                        onClick={() => setOpenConsentId(cf.form_id)}
                        className="whitespace-nowrap self-start mono-label border border-stone-900 px-3 py-2 hover:bg-stone-900 hover:text-white transition"
                      >
                        {cf.status === "reviewed_signed" ? "View Consent" : "Review & Sign"}
                      </button>
                    ) : (
                      <span className="whitespace-nowrap self-start mono-label text-stone-400 text-[10px] px-3 py-2">
                        Not submitted
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {openConsentId && (
            <ConsentReviewDrawer
              formId={openConsentId}
              onClose={() => setOpenConsentId(null)}
              onSigned={() => { loadConsentForms(); onChange && onChange(); }}
            />
          )}
          {showBatchRecord && (
            <AppointmentDetailDrawer
              bookingId={b.id}
              onClose={() => setShowBatchRecord(false)}
              onUpdated={() => { onChange && onChange(); }}
            />
          )}
          <div className="mt-6 border-t hairline pt-4 space-y-3">
            <p className="mono-label">Move Booking</p>
            <button
              data-testid="open-move-picker"
              onClick={() => setMovePickerOpen((v) => !v)}
              className="w-full mono-label bg-stone-900 text-white py-3 hover:bg-[#C5A059] transition"
              style={{ color: "#fff" }}
            >
              {movePickerOpen ? "Cancel Move" : "Move Booking"}
            </button>
            {movePickerOpen && (
              <MovePicker booking={b} onDone={(fields) => {
                setMovePickerOpen(false);
                onLocalPatch && onLocalPatch(b.id, fields);
                setB((prev) => ({ ...prev, ...fields }));
                onChange && onChange();
              }} />
            )}
            <label className="flex items-start gap-2 text-xs text-stone-700 pt-1">
              <input
                data-testid="toggle-available-earlier"
                type="checkbox"
                className="mt-1"
                checked={!!b.available_earlier}
                onChange={async (e) => {
                  const on = e.target.checked;
                  setB((prev) => ({ ...prev, available_earlier: on }));
                  onLocalPatch && onLocalPatch(b.id, { available_earlier: on });
                  try {
                    await api.patch(`/bookings/${b.id}`, { available_earlier: on });
                    toast.success(on ? "Marked available for earlier" : "Removed earlier flag");
                    onChange && onChange();
                  } catch (_) { toast.error("Could not save"); }
                }}
              />
              <span>Available for earlier appointment (surfaces first when filling a slot).</span>
            </label>
            {(b.reschedule_history || []).length > 0 && (
              <details className="text-xs text-stone-600">
                <summary className="mono-label cursor-pointer">Reschedule history ({b.reschedule_history.length})</summary>
                <ul className="mt-2 space-y-1">
                  {b.reschedule_history.map((h, i) => (
                    <li key={i}>{h.from_date} · {h.from_time} → {h.to_date} · {h.to_time} <span className="text-stone-400">({(h.changed_at || "").slice(0, 16).replace("T", " ")})</span></li>
                  ))}
                </ul>
              </details>
            )}
          </div>
          <div className="mt-6 border-t hairline pt-4 space-y-2">
            <p className="mono-label">Actions</p>
            <div className="flex gap-2 flex-wrap">
              {b.pending_appointment_status && b.pending_appointment_status !== b.appointment_status && (
                <div data-testid="pending-status-notice"
                     className="w-full mb-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2">
                  Pending change to <strong>{b.pending_appointment_status}</strong> —
                  tap <strong>Undo</strong> in the top-right toast within 15 seconds to cancel.
                </div>
              )}
              {["Confirmed", "Completed", "No-Show", "Cancelled"].map((s) => {
                const isCurrent = b.appointment_status === s;
                const isPending = b.pending_appointment_status === s;
                const anotherPending = b.pending_appointment_status && !isPending;
                return (
                  <button
                    key={s}
                    data-testid={`status-${s}`}
                    onClick={() => update({ appointment_status: s })}
                    disabled={isCurrent || !!b.pending_appointment_status}
                    title={anotherPending ? "Another status change is pending — undo it first" : ""}
                    className={`mono-label border hairline px-3 py-2 transition ${
                      isCurrent ? "bg-stone-900 text-white border-stone-900 cursor-default" :
                      isPending ? "bg-amber-100 border-amber-400 text-amber-900" :
                      anotherPending ? "opacity-40 cursor-not-allowed" : "hover:bg-alt"
                    }`}
                    style={isCurrent ? { color: "#fff" } : {}}
                  >
                    {s}{isPending && " · PENDING"}
                  </button>
                );
              })}
            </div>
          </div>
          {b.appointment_status === "Completed" && (
            <div className="mt-6 border-t hairline pt-4" data-testid="aftercare-section">
              <p className="mono-label mb-2">Aftercare Email</p>
              <div className="flex items-center gap-2 flex-wrap">
                {b.aftercare_status === "sent" && (
                  <span data-testid="aftercare-badge-sent" className="mono-label border hairline px-3 py-2 bg-emerald-50 text-emerald-800 border-emerald-300 text-[10px]">
                    Aftercare Sent{b.aftercare_sent_at ? ` · ${new Date(b.aftercare_sent_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                  </span>
                )}
                {b.aftercare_status === "failed" && (
                  <span data-testid="aftercare-badge-failed" className="mono-label border hairline px-3 py-2 bg-red-50 text-red-800 border-red-300 text-[10px]">
                    Aftercare Failed
                  </span>
                )}
                {b.aftercare_status === "skipped" && (
                  <span data-testid="aftercare-badge-skipped" className="mono-label border hairline px-3 py-2 bg-amber-50 text-amber-800 border-amber-300 text-[10px]">
                    No aftercare content set for these treatments
                  </span>
                )}
                {!b.aftercare_status && (
                  <span data-testid="aftercare-badge-pending" className="mono-label border hairline px-3 py-2 bg-stone-50 text-stone-600 border-stone-300 text-[10px]">
                    Aftercare Not Sent
                  </span>
                )}
                {b.aftercare_viewed_at && (
                  <span data-testid="aftercare-badge-viewed" className="mono-label border hairline px-3 py-2 bg-sky-50 text-sky-800 border-sky-300 text-[10px]">
                    Viewed · {new Date(b.aftercare_viewed_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    {b.aftercare_view_count > 1 ? ` (${b.aftercare_view_count}×)` : ""}
                  </span>
                )}
                <button
                  data-testid="resend-aftercare"
                  onClick={async () => {
                    try {
                      const r = await api.post(`/admin/bookings/${b.id}/resend-aftercare`);
                      if (r.data?.ok) {
                        toast.success("Aftercare email sent");
                      } else {
                        toast.error(r.data?.aftercare_error || "Aftercare send failed");
                      }
                      try {
                        const g = await api.get(`/bookings/${b.id}`);
                        setB(g.data);
                        onLocalPatch && onLocalPatch(b.id, g.data);
                      } catch (_) {}
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Aftercare send failed");
                    }
                  }}
                  className="mono-label border hairline px-3 py-2 hover:bg-alt text-[10px]"
                >
                  {b.aftercare_email_sent ? "Resend Aftercare" : "Send Aftercare"}
                </button>
              </div>
              {b.aftercare_error && (
                <p className="mt-2 text-xs text-red-700" data-testid="aftercare-error">{b.aftercare_error}</p>
              )}
            </div>
          )}
          <BookingPaymentEditor key={`${b.id}-${b.amount_paid}-${b.subtotal}`} booking={b}
            onSaved={(nb) => { setB(nb); setAmt(nb.remaining_balance); onLocalPatch && onLocalPatch(nb.id, nb); onChange && onChange(); }} />
          {b.remaining_balance > 0 && (
            <div className="mt-6 border-t hairline pt-4">
              <p className="mono-label mb-2">Record Cash Payment (in clinic)</p>
              <div className="flex gap-2">
                <input data-testid="mark-paid-amount" type="number" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} className="flex-1 border hairline p-2 text-sm" />
                <button data-testid="mark-paid" onClick={markPaid} className="bg-stone-900 text-white px-4 mono-label" style={{ color: "#fff" }}>Mark Paid</button>
              </div>
            </div>
          )}
          <button onClick={onClose} className="mt-8 w-full mono-label border hairline py-3 hover:bg-alt">Close</button>
        </div>
      </div>
    </div>
  );
}

function Clients() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [openClient, setOpenClient] = useState(null);
  const [reviewClientId, setReviewClientId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const reload = () => api.get("/clients", { params: q ? { q } : {} }).then((r) => setRows(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(reload, 250); return () => clearTimeout(t); }, [q]);
  const exportCsv = async () => {
    const token = localStorage.getItem("clae_token");
    const r = await fetch(`${API}/export/bookings.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob(); const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = "clae-bookings.csv"; a.click();
  };
  return (
    <div>
      <div className="flex items-center gap-4 flex-wrap">
        <h2 className="font-serif text-4xl font-light">Clients</h2>
        <input data-testid="clients-search" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className="ml-auto border hairline p-2 text-sm" />
        <button
          data-testid="add-client-btn"
          onClick={() => setAddOpen(true)}
          className="border border-stone-900 px-4 py-2 mono-label hover:bg-stone-900 hover:text-white transition"
        >+ Add Client</button>
        <button data-testid="export-csv" onClick={exportCsv} className="border border-stone-900 px-4 py-2 mono-label flex items-center gap-2 hover:bg-stone-900 hover:text-white transition"><Download size={14} /> Export CSV</button>
      </div>
      <div className="mt-8 border hairline bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-alt mono-label"><tr><th className="text-left p-3">Name</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Postcode</th><th className="text-right p-3">Bookings</th><th className="text-right p-3">Spent</th><th className="text-right p-3">Outstanding</th><th></th></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t hairline row-hover cursor-pointer" onClick={() => setOpenClient(c.id)} data-testid={`client-row-${c.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{c.first_name} {c.last_name}</span>
                    {c.possible_duplicate_of && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setReviewClientId(c.id); }}
                        data-testid={`dup-flag-${c.id}`}
                        title="Possible existing client — review"
                        className="inline-flex items-center gap-1 text-[10px] text-amber-700 border border-amber-300 bg-amber-50 px-1.5 py-0.5"
                      >
                        <span aria-hidden="true">⚠</span>
                        <span>Possible duplicate</span>
                      </button>
                    )}
                  </div>
                </td>
                <td className="p-3">{c.email}<br /><span className="text-stone-500 text-xs">{c.phone}</span></td>
                <td className="p-3">{c.postcode}</td>
                <td className="p-3 text-right">{c.bookings_count}</td>
                <td className="p-3 text-right">{gbp2(c.total_spent)}</td>
                <td className="p-3 text-right">{gbp2(c.outstanding)}</td>
                <td className="p-3"><button data-testid={`view-client-${c.id}`} onClick={(e) => { e.stopPropagation(); setOpenClient(c.id); }} className="mono-label hover:text-stone-900">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="p-6 text-stone-500 text-sm">No clients yet.</p>}
      </div>
      {openClient && <ClientDrawer clientId={openClient} onClose={() => setOpenClient(null)} />}
      {reviewClientId && (
        <DuplicateReviewModal
          clientId={reviewClientId}
          onClose={() => setReviewClientId(null)}
          onDone={() => { setReviewClientId(null); reload(); }}
        />
      )}
      {addOpen && (
        <AddClientModal
          onClose={() => setAddOpen(false)}
          onCreated={(cid) => { setAddOpen(false); reload(); setOpenClient(cid); }}
          onOpenExisting={(cid) => { setAddOpen(false); setOpenClient(cid); }}
        />
      )}
    </div>
  );
}

function AddClientModal({ onClose, onCreated, onOpenExisting }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", dob: "", phone: "",
    address: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null); // {id, first_name, ...} → shown as banner
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const canSubmit = form.first_name.trim() && form.last_name.trim() && isValidEmail
    && form.dob && form.phone.trim();
  const submit = async () => {
    setBusy(true); setExisting(null);
    try {
      const r = await api.post("/admin/clients", form);
      toast.success(r.data.possible_duplicate
        ? "Client created — flagged as possible duplicate for review"
        : "Client created");
      onCreated(r.data.client.id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.client_id) {
        setExisting({ id: detail.client_id, reason: detail.reason });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not create client");
      }
    } finally { setBusy(false); }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-auto"
      onClick={busy ? undefined : onClose}
      data-testid="add-client-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-lg w-full border hairline p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="mono-label text-[10px] text-stone-500">CLIENTS</p>
            <h3 className="font-serif text-2xl text-stone-900 mt-1">Add a new client</h3>
            <p className="text-xs text-stone-500 mt-1">
              We'll check for an existing client with the same email or phone before creating.
            </p>
          </div>
          <button onClick={onClose} className="mono-label text-stone-500 hover:text-stone-900">×</button>
        </div>
        {existing && (
          <div
            className="mt-4 border border-amber-300 bg-amber-50 p-3 text-sm"
            data-testid="add-client-existing-banner"
          >
            <p className="mono-label text-[10px] text-amber-800">EXISTING CLIENT FOUND</p>
            <p className="text-stone-800 mt-1">
              A client with matching {existing.reason?.includes("email") ? "email" : "phone"} and date of birth already exists.
            </p>
            <button
              onClick={() => onOpenExisting(existing.id)}
              data-testid="add-client-open-existing"
              className="mt-2 mono-label text-[11px] px-3 py-1.5 bg-stone-900 text-white"
            >Open existing profile →</button>
          </div>
        )}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label>
            <span className="mono-label text-stone-600">First name *</span>
            <input data-testid="add-client-first-name" value={form.first_name} onChange={set("first_name")}
                   className="mt-1 w-full border hairline px-3 py-2 text-sm" />
          </label>
          <label>
            <span className="mono-label text-stone-600">Last name *</span>
            <input data-testid="add-client-last-name" value={form.last_name} onChange={set("last_name")}
                   className="mt-1 w-full border hairline px-3 py-2 text-sm" />
          </label>
          <label className="sm:col-span-2">
            <span className="mono-label text-stone-600">Email *</span>
            <input data-testid="add-client-email" type="email" value={form.email} onChange={set("email")}
                   className="mt-1 w-full border hairline px-3 py-2 text-sm" />
          </label>
          <label>
            <span className="mono-label text-stone-600">Date of birth *</span>
            <input data-testid="add-client-dob" type="date" value={form.dob} onChange={set("dob")}
                   className="mt-1 w-full border hairline px-3 py-2 text-sm" />
          </label>
          <label>
            <span className="mono-label text-stone-600">Phone *</span>
            <input data-testid="add-client-phone" type="tel" value={form.phone} onChange={set("phone")}
                   placeholder="07984 715 514" className="mt-1 w-full border hairline px-3 py-2 text-sm" />
          </label>
          <label className="sm:col-span-2">
            <span className="mono-label text-stone-600">Address</span>
            <input data-testid="add-client-address" value={form.address} onChange={set("address")}
                   className="mt-1 w-full border hairline px-3 py-2 text-sm" />
          </label>
          <label className="sm:col-span-2">
            <span className="mono-label text-stone-600">Notes</span>
            <textarea data-testid="add-client-notes" value={form.notes} onChange={set("notes")} rows={3}
                      className="mt-1 w-full border hairline px-3 py-2 text-sm" />
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} data-testid="add-client-cancel"
                  className="mono-label px-4 py-2 border hairline text-stone-700">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !canSubmit}
            data-testid="add-client-submit"
            className="mono-label px-4 py-2 bg-stone-900 text-white disabled:opacity-60"
          >{busy ? "Creating…" : "Create client"}</button>
        </div>
      </div>
    </div>
  );
}

function DuplicateReviewModal({ clientId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/clients/${clientId}/duplicate-candidate`).then((r) => setData(r.data));
  }, [clientId]);
  const doAcceptNew = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/clients/${clientId}/accept-as-new`);
      toast.success("Kept as a new client");
      onDone();
    } catch { toast.error("Could not update"); }
    finally { setBusy(false); }
  };
  const doLink = async () => {
    if (!data?.target?.id) return;
    if (!window.confirm(
      "Merge this profile INTO the existing client? All bookings, top-ups, consents and aftercare will move across, and this duplicate profile will be deleted. This cannot be undone.",
    )) return;
    setBusy(true);
    try {
      await api.post(`/admin/clients/${clientId}/link-to/${data.target.id}`);
      toast.success("Merged into existing client");
      onDone();
    } catch { toast.error("Merge failed"); }
    finally { setBusy(false); }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={busy ? undefined : onClose}
      data-testid="dup-review-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-2xl w-full border hairline p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="mono-label text-[10px] text-amber-700">POSSIBLE EXISTING CLIENT FOUND</p>
            <h3 className="font-serif text-2xl text-stone-900 mt-1">Review before creating a new client</h3>
            {data?.reason && (
              <p className="text-xs text-stone-500 mt-1" data-testid="dup-review-reason">
                Reason: {data.reason.replace(/_/g, " ")}
              </p>
            )}
          </div>
          <button onClick={onClose} className="mono-label text-stone-500 hover:text-stone-900">×</button>
        </div>
        {!data ? (
          <p className="mt-6 text-sm text-stone-500">Loading…</p>
        ) : !data.has_candidate ? (
          <p className="mt-6 text-sm text-stone-500">No candidate found. It's safe to keep as a new client.</p>
        ) : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { title: "NEW ENTRY", c: data.source, testid: "dup-source" },
              { title: "EXISTING CLIENT", c: data.target, testid: "dup-target" },
            ].map(({ title, c, testid }) => (
              <div key={title} className="border hairline p-4" data-testid={testid}>
                <p className="mono-label text-[10px] text-stone-500">{title}</p>
                <p className="font-serif text-lg text-stone-900 mt-1">{c?.first_name} {c?.last_name}</p>
                <p className="text-xs text-stone-600 mt-1">{c?.email}</p>
                <p className="text-xs text-stone-600">{c?.phone}</p>
                <p className="text-xs text-stone-600">DOB · {c?.dob || "—"}</p>
              </div>
            ))}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2 flex-wrap">
          <button
            onClick={doAcceptNew}
            disabled={busy}
            data-testid="dup-accept-new"
            className="mono-label px-4 py-2 border hairline text-stone-700"
          >Create as new client</button>
          {data?.has_candidate && (
            <button
              onClick={doLink}
              disabled={busy}
              data-testid="dup-link-existing"
              className="mono-label px-4 py-2 bg-stone-900 text-white disabled:opacity-60"
            >Link to existing client</button>
          )}
        </div>
      </div>
    </div>
  );
}

function AccordionSection({ title, count, defaultOpen = false, children, testid, headerRight }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-8 border-t hairline pt-6" data-testid={testid}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left group gap-3"
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
      >
        <p className="mono-label group-hover:text-stone-900 transition">
          {title}{typeof count === "number" ? ` (${count})` : ""}
        </p>
        <span className="flex items-center gap-3 shrink-0">
          {headerRight}
          <span className="mono-label text-stone-400 text-[10px]" aria-hidden>
            {open ? "▴ HIDE" : "▾ SHOW"}
          </span>
        </span>
      </button>
      {open && <div className="mt-4" data-testid={`${testid}-body`}>{children}</div>}
    </div>
  );
}

/* ── Client-profile referral row + summary pill ────────────────
   Renders one referral inside the Client Profile drawer's Referrals
   accordion. Same colour language as the Admin Referrals dashboard
   so a glance is enough to know what stage each referral is at. */
function SummaryPill({ label, value, gold: goldBg, mono }) {
  return (
    <div className={`border hairline p-2 ${goldBg ? "bg-amber-50/60" : "bg-stone-50"}`} data-testid={`summary-pill-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="mono-label text-[9px] text-stone-500">{label}</p>
      <p className={`text-sm mt-0.5 ${mono ? "font-mono" : ""} ${goldBg ? "gold" : ""}`}>{value}</p>
    </div>
  );
}
function ClientReferralRow({ r, showReferrer = false }) {
  const statusMeta = {
    ACTIVE:   { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "🟢 ACTIVE" },
    AWAITING: { cls: "bg-amber-50 text-amber-800 border-amber-200",       label: "🟡 AWAITING APPOINTMENT" },
    INACTIVE: { cls: "bg-stone-100 text-stone-600 border-stone-200",      label: "⚪ INACTIVE" },
  }[r.simple_status] || { cls: "bg-stone-50 text-stone-600 border-stone-200", label: r.simple_status };
  const creditMeta = {
    AVAILABLE:   { cls: "bg-emerald-100 text-emerald-900 border-emerald-300", label: `${gbp2(r.referral_credit)} ACTIVE` },
    PENDING:     { cls: "bg-amber-50 text-amber-800 border-amber-200",         label: `${gbp2(r.referral_credit)} PENDING` },
    USED:        { cls: "bg-stone-100 text-stone-500 border-stone-200",        label: `${gbp2(r.referral_credit)} USED` },
    INVALIDATED: { cls: "bg-red-50 text-red-700 border-red-200",               label: "INVALIDATED" },
    EXPIRED:     { cls: "bg-stone-200 text-stone-700 border-stone-300",        label: `${gbp2(r.referral_credit)} EXPIRED` },
    NONE:        { cls: "bg-stone-50 text-stone-400 border-stone-200",         label: "£0" },
  }[r.credit_state] || { cls: "bg-stone-50 text-stone-600 border-stone-200", label: r.credit_state };
  const expiryMeta = {
    normal:        { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "NORMAL" },
    approaching:   { cls: "bg-amber-50 text-amber-800 border-amber-200",       label: "APPROACHING" },
    expiring_soon: { cls: "bg-red-50 text-red-700 border-red-200",             label: "EXPIRING SOON" },
    expired:       { cls: "bg-stone-200 text-stone-700 border-stone-300",      label: "EXPIRED" },
  }[r.expiry_state];
  const who = showReferrer ? (r.referrer_name || "—") : (r.referred_client_name || "—");
  const whoEmail = showReferrer ? r.referrer_email : r.referred_client_email;
  const discount = Number(r.referral_discount || 0);
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } };
  return (
    <div className="border hairline p-3 mb-2" data-testid={`profile-referral-${r.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{who}</p>
          <p className="text-xs text-stone-500">
            {whoEmail}
            {r.referral_code ? <> · <span className="font-mono">{r.referral_code}</span></> : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <span className={`mono-label text-[10px] border px-2 py-0.5 ${statusMeta.cls}`}>{statusMeta.label}</span>
          <span className={`mono-label text-[10px] border px-2 py-0.5 ${creditMeta.cls}`}>{creditMeta.label}</span>
          {expiryMeta && (
            <span className={`mono-label text-[10px] border px-2 py-0.5 ${expiryMeta.cls}`} data-testid={`profile-expiry-${r.expiry_state}`}>{expiryMeta.label}</span>
          )}
        </div>
      </div>
      <p className="text-[11px] text-stone-500 mt-1">
        Appointment {r.appointment_date || "—"}
        {r.booking_reference ? <> · <span className="font-mono">{r.booking_reference}</span></> : null}
        {discount > 0 ? <> · Discount − {gbp2(discount)}</> : null}
        {r.expires_at ? <> · Expires {fmt(r.expires_at)}{r.days_remaining != null ? <> · {r.days_remaining} days left</> : null}</> : null}
      </p>
    </div>
  );
}

// At-a-glance chip for the collapsed Reminder Status accordion. Prioritises
// showing an error over any success state so problems are never hidden behind
// a "SENT" badge just because one of the three reminders went out.
function ReminderStatusChip({ booking }) {
  const b = booking || {};
  const consentApplicable = !b.is_consultation && !b.is_topup;
  const flags = [
    { sent: b.reminder_48h_sent, sentAt: b.reminder_48h_sent_at, err: b.reminder_48h_error, skip: b.reminder_48h_skipped_reason, sched: b.reminder_48h_scheduled_at, applies: true },
    { sent: b.reminder_24h_sent, sentAt: b.reminder_24h_sent_at, err: b.reminder_24h_error, skip: b.reminder_24h_skipped_reason, sched: b.reminder_24h_scheduled_at, applies: true },
    { sent: b.consent_reminder_sent, sentAt: b.consent_reminder_sent_at, err: null, skip: b.consent_reminder_skipped_reason, sched: b.consent_reminder_scheduled_at, applies: consentApplicable },
  ].filter((r) => r.applies);
  if (flags.some((r) => r.err)) {
    return <span data-testid="reminder-status-chip" className="mono-label inline-block px-2 py-0.5 border border-red-300 bg-red-50 text-red-700 text-[10px]">ERROR</span>;
  }
  if (flags.length && flags.every((r) => r.sent && r.sentAt && !r.skip)) {
    return <span data-testid="reminder-status-chip" className="mono-label inline-block px-2 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px]">SENT</span>;
  }
  if (flags.some((r) => r.sent && r.skip)) {
    return <span data-testid="reminder-status-chip" className="mono-label inline-block px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 text-[10px]">PARTIAL</span>;
  }
  if (flags.some((r) => r.sched)) {
    return <span data-testid="reminder-status-chip" className="mono-label inline-block px-2 py-0.5 border border-stone-300 bg-stone-50 text-stone-700 text-[10px]">SCHEDULED</span>;
  }
  return <span data-testid="reminder-status-chip" className="mono-label inline-block px-2 py-0.5 border border-stone-300 bg-stone-50 text-stone-500 text-[10px]">NOT SET</span>;
}

function AppointmentDetailDrawer({ bookingId, onClose, onUpdated }) {
  const [record, setRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pracName, setPracName] = useState("");
  const [treatNotes, setTreatNotes] = useState("");
  const [pracNotes, setPracNotes] = useState("");
  const [itemsDraft, setItemsDraft] = useState([]);
  const [eligibleByTid, setEligibleByTid] = useState({});
  const [extraDraft, setExtraDraft] = useState([]);
  const [allStock, setAllStock] = useState([]);
  const load = React.useCallback(() => {
    api.get(`/bookings/${bookingId}/record`).then(async (r) => {
      setRecord(r.data);
      const b = r.data.booking || {};
      setPracName(b.practitioner_name || "Claudia");
      setTreatNotes(b.treatment_notes || "");
      setPracNotes(b.practitioner_notes || "");
      const draft = (b.items || []).map((i) => ({
        treatment_id: i.treatment_id, name: i.name,
        stock_item_id: i.product_batch?.stock_item_id || "",
        product_name: i.product_batch?.product_name || "",
        batch_number: i.product_batch?.batch_number || "",
        batch_expiry: i.product_batch?.batch_expiry || "",
        quantity_used: i.product_batch?.quantity_used || "",
      }));
      setItemsDraft(draft);
      // Preserve any previously-saved multi-product extras exactly so
      // their entry ids survive a re-open (idempotent deduct relies
      // on stable ids). Fall back to an empty list on a fresh booking.
      setExtraDraft((b.extra_product_batches || []).map((e) => ({
        id: e.id,
        stock_item_id: e.stock_item_id || "",
        product_name: e.product_name || "",
        batch_number: e.batch_number || "",
        batch_expiry: e.batch_expiry || "",
        quantity_used: e.quantity_used || "",
      })));
      // Fetch the full stock list once so the "+ Add Another Product"
      // dropdown exposes every product the clinic has (not restricted
      // per-treatment — these are extras recorded against the whole
      // appointment).
      try {
        const sr = await api.get("/admin/stock");
        setAllStock(sr.data?.items || []);
      } catch (_) { /* non-fatal — UI still works, dropdown will show nothing */ }
      // Fetch eligible stock items per treatment so the Product dropdown
      // only lists products the admin has mapped to each treatment.
      const tids = [...new Set(draft.map((d) => d.treatment_id).filter(Boolean))];
      if (tids.length) {
        try {
          const er = await api.get("/admin/stock/eligible-for-treatments", { params: { treatment_ids: tids.join(",") } });
          const by = er.data?.by_treatment || {};
          setEligibleByTid(by);
          // Auto-select the single eligible product for treatments with
          // exactly one mapping — matches scenario A in the spec and
          // preserves the current simple workflow.
          setItemsDraft((prev) => prev.map((d) => {
            if (d.stock_item_id) return d;
            const opts = (by[d.treatment_id] || []).filter((o) => o.available);
            if (opts.length === 1) {
              return { ...d, stock_item_id: opts[0].id, product_name: d.product_name || opts[0].name };
            }
            return d;
          }));
        } catch (_) { /* soft-fail — admin can still save a free-text product_name */ }
      }
    });
  }, [bookingId]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        practitioner_name: pracName,
        treatment_notes: treatNotes,
        practitioner_notes: pracNotes,
        items: itemsDraft.map(({ treatment_id, stock_item_id, product_name, batch_number, batch_expiry, quantity_used }) =>
          ({ treatment_id, stock_item_id, product_name, batch_number, batch_expiry, quantity_used })),
        extra_product_batches: extraDraft.map(({ id, stock_item_id, product_name, batch_number, batch_expiry, quantity_used }) =>
          ({ id, stock_item_id, product_name, batch_number, batch_expiry, quantity_used })),
      };
      const resp = await api.patch(`/bookings/${bookingId}/treatment-record`, body);
      const warns = resp?.data?.warnings || [];
      if (warns.length) {
        warns.forEach((w) => toast.warning(w));
      } else {
        toast.success("Treatment record saved");
      }
      onUpdated && onUpdated();
      load();
    } catch (e) { toast.error("Save failed — please try again"); }
    setSaving(false);
  };
  const patchItem = (idx, key, val) =>
    setItemsDraft((d) => d.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  const patchExtra = (idx, key, val) =>
    setExtraDraft((d) => d.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  const addExtra = () => setExtraDraft((d) => [
    ...d,
    {
      // Stable client-generated id so the backend can map subsequent
      // saves to the same stock_history ledger (idempotent deducts).
      id: (window.crypto?.randomUUID?.() || `x-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
      stock_item_id: "",
      product_name: "",
      batch_number: "",
      batch_expiry: "",
      quantity_used: "",
    },
  ]);
  const removeExtra = (idx) =>
    setExtraDraft((d) => d.filter((_, i) => i !== idx));

  if (!record) {
    return (
      <div className="fixed inset-0 z-50 bg-stone-900/40" onClick={onClose}>
        <div className="absolute right-0 top-0 h-full w-full sm:max-w-2xl bg-white p-8" onClick={(e) => e.stopPropagation()}>
          <p className="mono-label">Loading appointment record…</p>
        </div>
      </div>
    );
  }
  const b = record.booking || {};
  const link = record.topup_link || {};
  const consents = record.consent_forms || [];
  const consentApplicable = !b.is_consultation && !b.is_topup;

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40" onClick={onClose} data-testid="appt-detail-drawer">
      <div className="absolute right-0 top-0 h-full w-full sm:max-w-2xl bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-8">
          <div className="flex items-start justify-between">
            <div>
              <p className="mono-label text-stone-400 text-[10px]">APPOINTMENT / TREATMENT RECORD</p>
              <h3 className="font-serif text-3xl mt-1">{b.date} · {b.time}</h3>
              <p className="text-sm text-stone-600 mt-1 font-mono">{b.reference}</p>
            </div>
            <StatusPill booking={b} />
          </div>

          {/* Appointment Details */}
          <div className="mt-6 border-t hairline pt-4">
            <p className="mono-label mb-3">Appointment Details</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Client" value={b.client_name || "—"} />
              <Info label="Email" value={b.client_email || "—"} />
              <Info label="Phone" value={b.client_phone || "—"} />
              <Info label="Date" value={b.date || "—"} />
              <Info label="Time" value={b.time || "—"} />
              <Info label="Duration" value={`${b.duration_min || 0} min`} />
              <Info label="Status" value={b.appointment_status || "—"} />
              <Info label="Payment" value={b.payment_status || "—"} />
              <Info label="Reference" value={b.reference || "—"} />
              <Info label="Booked" value={(b.created_at || "").slice(0, 16).replace("T", " ")} />
            </div>
          </div>

          {/* Treatment Details */}
          <div className="mt-6 border-t hairline pt-4">
            <p className="mono-label mb-3">Treatment Details</p>
            <ul className="space-y-2 text-sm">
              {(b.items || []).map((i, idx) => {
                const qty = i.qty || i.quantity || 1;
                const price = i.price ?? i.price_at_booking ?? 0;
                return (
                  <li key={idx} className="flex justify-between">
                    <span>{i.name} × {qty}</span>
                    <span className="font-mono">{gbp2(price * qty)}</span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-3">
              <label className="mono-label text-[10px] text-stone-500">Treatment notes</label>
              <textarea
                data-testid="treatment-notes-input"
                rows={3} value={treatNotes} onChange={(e) => setTreatNotes(e.target.value)}
                placeholder="Areas treated, technique, findings…"
                className="w-full border hairline p-2 text-sm mt-1"
              />
            </div>
          </div>

          {/* Practitioner Notes */}
          <div className="mt-6 border-t hairline pt-4">
            <p className="mono-label mb-3">Practitioner Notes</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="mono-label text-[10px] text-stone-500">Practitioner</label>
                <input
                  data-testid="practitioner-name-input"
                  value={pracName} onChange={(e) => setPracName(e.target.value)}
                  className="w-full border hairline p-2 text-sm mt-1"
                />
              </div>
            </div>
            <textarea
              data-testid="practitioner-notes-input"
              rows={4} value={pracNotes} onChange={(e) => setPracNotes(e.target.value)}
              placeholder="Aftercare given, follow-ups, private observations…"
              className="w-full border hairline p-2 text-sm"
            />
          </div>

          {/* Product & Batch Record */}
          <div className="mt-6 border-t hairline pt-4" data-testid="product-batch-panel">
            <p className="mono-label mb-3">Product &amp; Batch Record</p>
            <p className="text-[11px] text-stone-500 mb-3">
              These fields are stored on THIS specific appointment and are never overwritten by future appointments.
            </p>
            <ul className="space-y-4">
              {itemsDraft.map((it, idx) => {
                const opts = eligibleByTid[it.treatment_id] || [];
                const hasMultiple = opts.length > 1;
                const hasNone = opts.length === 0;
                return (
                <li key={idx} className="border hairline p-3" data-testid={`batch-row-${idx}`}>
                  <p className="mono-label text-[10px] text-stone-500 mb-2">{it.name}</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <label className="mono-label text-[10px] text-stone-500">Product (from stocklist)</label>
                      {hasNone ? (
                        <>
                          <input data-testid={`batch-product-${idx}`}
                                 value={it.product_name}
                                 onChange={(e) => patchItem(idx, "product_name", e.target.value)}
                                 placeholder="e.g. Botox 100u"
                                 className="w-full border hairline p-2 text-sm mt-1" />
                          <p className="text-[10px] text-amber-700 mt-1" data-testid={`batch-no-mapping-${idx}`}>
                            No eligible stock products linked to this treatment. Add a product mapping in Settings → Stock, or type a name here to record the appointment only (no stock deducted).
                          </p>
                        </>
                      ) : (
                        <>
                          <select data-testid={`batch-product-select-${idx}`}
                                  value={it.stock_item_id || ""}
                                  onChange={(e) => {
                                    const sid = e.target.value;
                                    const chosen = opts.find((o) => o.id === sid);
                                    patchItem(idx, "stock_item_id", sid);
                                    if (chosen) patchItem(idx, "product_name", chosen.name);
                                  }}
                                  className="w-full border hairline p-2 text-sm mt-1 bg-white">
                            <option value="">{hasMultiple ? "Select the product used…" : "—"}</option>
                            {opts.map((o) => (
                              <option key={o.id} value={o.id} disabled={!o.available}>
                                {o.name}{o.available ? ` · ${o.current_qty.toFixed(2)} in stock` : " · OUT OF STOCK"}
                              </option>
                            ))}
                          </select>
                          {!hasMultiple && it.stock_item_id && (
                            <p className="text-[10px] text-stone-500 mt-1" data-testid={`batch-auto-selected-${idx}`}>
                              Only one eligible product — auto-selected.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    <div>
                      <label className="mono-label text-[10px] text-stone-500">Batch / Lot number</label>
                      <input data-testid={`batch-number-${idx}`}
                             value={it.batch_number} onChange={(e) => patchItem(idx, "batch_number", e.target.value)}
                             placeholder="e.g. ABC12345" className="w-full border hairline p-2 text-sm mt-1" />
                    </div>
                    <div>
                      <label className="mono-label text-[10px] text-stone-500">Batch expiry</label>
                      <input data-testid={`batch-expiry-${idx}`}
                             value={it.batch_expiry} onChange={(e) => patchItem(idx, "batch_expiry", e.target.value)}
                             placeholder="e.g. 06/2028" className="w-full border hairline p-2 text-sm mt-1" />
                    </div>
                    <div>
                      <label className="mono-label text-[10px] text-stone-500">Quantity used</label>
                      <input data-testid={`batch-quantity-${idx}`}
                             value={it.quantity_used} onChange={(e) => patchItem(idx, "quantity_used", e.target.value)}
                             placeholder="e.g. 20 units" className="w-full border hairline p-2 text-sm mt-1" />
                    </div>
                  </div>
                </li>
              );})}
            </ul>

            {/* Additional products — multi-product-per-appointment extras.
                Each entry is independent of the per-treatment list above
                and deducts its own quantity from the chosen stock item
                on save. Removing an unsaved entry is purely local; a
                saved entry's deducted qty is restored by the backend
                when it's removed and the record re-saved. */}
            <div className="mt-5 border-t hairline pt-4" data-testid="extra-product-batches">
              <div className="flex items-center justify-between mb-2">
                <p className="mono-label text-[11px] text-stone-500">Additional products used</p>
                <button
                  type="button"
                  data-testid="add-another-product-btn"
                  onClick={addExtra}
                  className="mono-label text-[10px] border hairline px-3 py-1 hover:bg-stone-50"
                >
                  + Add Another Product
                </button>
              </div>
              {extraDraft.length === 0 ? (
                <p className="text-[11px] text-stone-400">
                  Record any extra products used during this appointment. Each entry deducts its own quantity from the stock list on save.
                </p>
              ) : (
                <ul className="space-y-3">
                  {extraDraft.map((ex, idx) => (
                    <li key={ex.id || idx} className="border hairline p-3" data-testid={`extra-product-row-${idx}`}>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <label className="mono-label text-[10px] text-stone-500">Product (from stocklist)</label>
                          <select
                            data-testid={`extra-product-select-${idx}`}
                            value={ex.stock_item_id || ""}
                            onChange={(e) => {
                              const sid = e.target.value;
                              const chosen = (allStock || []).find((s) => s.id === sid);
                              patchExtra(idx, "stock_item_id", sid);
                              if (chosen) patchExtra(idx, "product_name", chosen.name);
                            }}
                            className="w-full border hairline p-2 text-sm mt-1 bg-white"
                          >
                            <option value="">Select the product used…</option>
                            {(allStock || []).map((s) => (
                              <option key={s.id} value={s.id} disabled={(s.current_qty || 0) <= 0}>
                                {s.name}{(s.current_qty || 0) > 0 ? ` · ${Number(s.current_qty).toFixed(2)} in stock` : " · OUT OF STOCK"}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mono-label text-[10px] text-stone-500">Batch / Lot number</label>
                          <input
                            data-testid={`extra-batch-number-${idx}`}
                            value={ex.batch_number}
                            onChange={(e) => patchExtra(idx, "batch_number", e.target.value)}
                            placeholder="e.g. ABC12345"
                            className="w-full border hairline p-2 text-sm mt-1"
                          />
                        </div>
                        <div>
                          <label className="mono-label text-[10px] text-stone-500">Batch expiry</label>
                          <input
                            data-testid={`extra-batch-expiry-${idx}`}
                            value={ex.batch_expiry}
                            onChange={(e) => patchExtra(idx, "batch_expiry", e.target.value)}
                            placeholder="e.g. 06/2028"
                            className="w-full border hairline p-2 text-sm mt-1"
                          />
                        </div>
                        <div>
                          <label className="mono-label text-[10px] text-stone-500">Quantity used</label>
                          <input
                            data-testid={`extra-quantity-${idx}`}
                            value={ex.quantity_used}
                            onChange={(e) => patchExtra(idx, "quantity_used", e.target.value)}
                            placeholder="e.g. 20 or 0.5"
                            className="w-full border hairline p-2 text-sm mt-1"
                          />
                        </div>
                      </div>
                      <div className="mt-2 flex justify-end">
                        <button
                          type="button"
                          data-testid={`extra-remove-${idx}`}
                          onClick={() => removeExtra(idx)}
                          className="mono-label text-[10px] text-stone-500 hover:text-red-700 underline"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Payment */}
          <div className="mt-6 border-t hairline pt-4">
            <p className="mono-label mb-3">Payment</p>
            <div className="grid grid-cols-3 gap-3">
              <Info label="Total" value={gbp2(b.total || 0)} />
              <Info label="Paid" value={gbp2(b.amount_paid || 0)} />
              <Info label="Balance" value={gbp2(b.remaining_balance || 0)} />
            </div>
            <p className="mono-label text-[10px] text-stone-400 mt-2">STATUS · {b.payment_status || "—"}</p>
          </div>

          {/* Consent */}
          <div className="mt-6 border-t hairline pt-4" data-testid="record-consent-section">
            <p className="mono-label mb-3">Consent</p>
            {!consentApplicable ? (
              <p className="text-[12px] text-stone-500">
                Not applicable — {b.is_topup ? "top-up appointments reuse the client's original Anti-Wrinkle consent." : "consultations do not require a treatment consent form."}
              </p>
            ) : (
              <>
                {/* Email delivery + first-opened indicators sit above the
                    per-form chips so admins can see the full lifecycle:
                    Sent → Opened → Signed (plus review status via the
                    existing chip). */}
                <div className="grid grid-cols-2 gap-2 mb-3 text-[11px]" data-testid="record-consent-timeline">
                  <div className="border hairline p-2">
                    <p className="mono-label text-[10px] text-stone-400">EMAIL SENT</p>
                    <p className="text-stone-900 mt-1" data-testid="consent-email-sent-at">
                      {b.consent_email_sent_at
                        ? (b.consent_email_sent_at || "").slice(0, 16).replace("T", " ")
                        : (b.consent_email_sent ? "Sent" : "Not sent yet")}
                    </p>
                    {!b.consent_email_sent && b.consent_email_error && (
                      <p className="text-red-700 mt-1" data-testid="consent-email-error">Failed: {b.consent_email_error}</p>
                    )}
                  </div>
                  <div className="border hairline p-2">
                    <p className="mono-label text-[10px] text-stone-400">OPENED / REVIEWED</p>
                    <p className="text-stone-900 mt-1" data-testid="consent-form-opened-at">
                      {b.consent_form_opened_at
                        ? (b.consent_form_opened_at || "").slice(0, 16).replace("T", " ")
                        : "Not opened yet"}
                    </p>
                  </div>
                </div>
                {consents.length === 0 ? (
                  <p className="text-[12px] text-stone-500">No consent form recorded for this booking.</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {consents.map((f) => (
                      <li key={f.id} className="flex justify-between items-center border hairline p-2">
                        <div>
                          <p className="text-stone-900">{f.treatment_name}</p>
                          <p className="mono-label text-[10px] text-stone-400">
                            {f.submitted_at ? `Signed ${(f.submitted_at || "").slice(0, 16).replace("T", " ")}` : "Not signed"}
                            {f.reviewed_at ? ` · Reviewed ${(f.reviewed_at || "").slice(0, 16).replace("T", " ")}` : ""}
                          </p>
                        </div>
                        <ConsentStatusChip status={f.status || "submitted_awaiting_review"} testid={`record-cf-chip-${f.id}`} />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* Top-Up Information */}
          {(link.is_topup || (link.topup_entitlements || []).length > 0) && (
            <div className="mt-6 border-t hairline pt-4" data-testid="record-topup-section">
              <p className="mono-label mb-3">Top-Up Information</p>
              {link.is_topup && link.original_booking && (
                <div className="text-sm space-y-2">
                  <p className="text-stone-500 text-[12px]">This appointment is a top-up. Original treatment:</p>
                  <div className="border hairline p-3">
                    <p className="mono-label text-[10px] text-stone-400">{link.original_booking.reference}</p>
                    <p className="text-stone-900">{link.original_booking.date} · {link.original_booking.time}</p>
                    <p className="text-stone-600 text-[12px]">
                      {(link.original_booking.items || []).map((i) => i.name).join(", ")}
                    </p>
                    <p className="mono-label text-[10px] text-stone-400 mt-1">STATUS · {link.original_booking.appointment_status}</p>
                  </div>
                </div>
              )}
              {(link.topup_entitlements || []).map((t) => (
                <div key={t.id} className="border hairline p-3 text-sm mt-2" data-testid={`topup-entitlement-${t.id}`}>
                  <p className="mono-label text-[10px] text-stone-400">TOP-UP ENTITLEMENT</p>
                  <p className="text-stone-600 text-[12px]">
                    Window {t.topup_start_date} → {t.topup_end_date} · Status: {t.status}
                  </p>
                  {t.topup_booking_date && (
                    <p className="text-stone-600 text-[12px]">Booked for {t.topup_booking_date} · {t.topup_booking_time}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex gap-2">
            <button onClick={onClose} className="flex-1 mono-label border hairline py-3 hover:bg-alt" data-testid="record-close">Close</button>
            <button onClick={save} disabled={saving} data-testid="record-save"
                    className="flex-1 mono-label bg-stone-900 text-white py-3 disabled:opacity-40 hover:bg-[#C5A059] transition"
                    style={{ color: "#fff" }}>
              {saving ? "Saving…" : "Save Record"}
            </button>
          </div>
          {b.treatment_record_updated_at && (
            <p className="mono-label text-[10px] text-stone-400 mt-3">
              LAST EDIT · {(b.treatment_record_updated_at || "").slice(0, 16).replace("T", " ")} · {b.treatment_record_updated_by}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientDrawer({ clientId, onClose }) {
  const [client, setClient] = useState(null);
  const [forms, setForms] = useState([]);
  const [referrals, setReferrals] = useState({ referred_by: [], clients_referred: [], summary: null });
  const [packages, setPackages] = useState([]);
  const [openConsentId, setOpenConsentId] = useState(null);
  const [openApptId, setOpenApptId] = useState(null);
  const loadForms = React.useCallback(() => {
    api.get(`/consent-forms/client/${clientId}`).then((r) => setForms(r.data));
  }, [clientId]);
  const loadClient = React.useCallback(() => {
    api.get(`/clients/${clientId}`).then((r) => setClient(r.data));
  }, [clientId]);
  const loadReferrals = React.useCallback(() => {
    api.get(`/admin/referrals/by-client/${clientId}`)
      .then((r) => setReferrals(r.data))
      .catch(() => setReferrals({ referred_by: [], clients_referred: [], summary: null }));
  }, [clientId]);
  const loadPackages = React.useCallback(() => {
    api.get(`/admin/packages`, { params: { client_id: clientId } })
      .then((r) => setPackages(r.data || []))
      .catch(() => setPackages([]));
  }, [clientId]);
  useEffect(() => { loadClient(); loadForms(); loadReferrals(); loadPackages(); /* eslint-disable-next-line */ }, [loadClient, loadForms, loadReferrals, loadPackages]);
  const downloadForm = async (fid, ref) => {
    const token = localStorage.getItem("clae_token");
    const r = await fetch(`${API}/consent-forms/${fid}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Could not download form"); return; }
    const blob = await r.blob(); const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = `clae-form-${ref}.pdf`; a.click();
    URL.revokeObjectURL(u);
  };
  // Sort appointments most-recent first (spec §Appointment History).
  const sortedBookings = useMemo(() => {
    const bs = [...(client?.bookings || [])];
    return bs.sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));
  }, [client]);
  return (
    <div className="fixed inset-0 z-40 bg-stone-900/30" onClick={onClose}>
      <div className="absolute right-0 top-0 h-full w-full sm:max-w-xl bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {!client ? <p className="p-8 mono-label">Loading…</p> : (
          <div className="p-8">
            <p className="mono-label">Client Profile</p>
            <h3 className="font-serif text-3xl mt-2">{client.first_name} {client.last_name}</h3>
            <p className="text-sm text-stone-600 mt-1">{client.email} · {client.phone}</p>
            <p className="text-sm text-stone-600">{client.address}, {client.postcode}</p>

            <div className="mt-6 grid grid-cols-3 gap-3">
              <Info label="Bookings" value={(client.bookings || []).length} />
              <Info label="Credit" value={gbp2(client.credit_balance || 0)} />
              <Info label="Referral Code" value={client.referral_code || "—"} />
            </div>

            {/* Referrals — surfaces `referred_by` + `clients_referred` from
                /admin/referrals/by-client + the client's credit ledger
                summary. Collapsed by default; the count reflects any
                referral activity (in or out). */}
            <AccordionSection
              title="Referrals"
              count={(referrals.referred_by?.length || 0) + (referrals.clients_referred?.length || 0)}
              testid="client-referrals-accordion"
            >
              {/* Always-on summary strip — makes the referral state of
                  every client instantly readable, even for someone who
                  has never referred or been referred. */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="client-referral-summary">
                <SummaryPill label="PEOPLE REFERRED"  value={referrals.summary?.people_referred ?? 0} />
                <SummaryPill label="COMPLETED"        value={referrals.summary?.completed_referrals ?? 0} />
                <SummaryPill label="PENDING"          value={referrals.summary?.pending_referrals ?? 0} />
                <SummaryPill label="TOTAL EARNED"     value={gbp2(referrals.summary?.total_earned ?? 0)} gold />
                <SummaryPill label="ACTIVE CREDIT"    value={gbp2(referrals.summary?.active_credit ?? 0)} />
                <SummaryPill label="USED CREDIT"      value={gbp2(referrals.summary?.used_credit ?? 0)} />
                <SummaryPill label="PENDING CREDIT"   value={gbp2(referrals.summary?.pending_credit ?? 0)} />
                <SummaryPill label="REFERRAL CODE"    value={client.referral_code || "—"} mono />
              </div>

              {(referrals.referred_by?.length === 0 && referrals.clients_referred?.length === 0) && (
                <p className="text-sm text-stone-500">
                  This client hasn't referred anyone yet, and wasn't referred by another client.
                </p>
              )}

              {referrals.referred_by?.length > 0 && (
                <div className="mb-5" data-testid="referred-by-section">
                  <p className="mono-label text-stone-500 mb-2">REFERRED BY</p>
                  {referrals.referred_by.map((r) => (
                    <ClientReferralRow key={r.id} r={r} showReferrer />
                  ))}
                </div>
              )}

              {referrals.clients_referred?.length > 0 && (
                <div data-testid="clients-referred-section">
                  <p className="mono-label text-stone-500 mb-2">
                    CLIENTS REFERRED · {referrals.clients_referred.length}
                  </p>
                  {referrals.clients_referred.map((r) => (
                    <ClientReferralRow key={r.id} r={r} />
                  ))}
                </div>
              )}
            </AccordionSection>

            {/* Collapsible: Consent Forms — collapsed by default */}
            <AccordionSection title="Consent Forms" count={forms.length} testid="consent-forms-accordion">
              {forms.length === 0 ? (
                <p className="text-sm text-stone-500">No consent forms yet. Clients receive a consent link by email after paying their deposit.</p>
              ) : (
                <ul className="space-y-3">
                  {forms.map((f) => (
                    <li key={f.id} className="border hairline p-4" data-testid={`form-row-${f.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="mono-label">{f.template_title}</p>
                            <span className="mono-label text-stone-400 text-[10px]">v{f.form_version || 1}</span>
                          </div>
                          <p className="text-sm text-stone-900 mt-1">{f.treatment_name}</p>
                          <p className="text-xs text-stone-500 mt-1">
                            Booking <span className="font-mono">{f.booking_reference}</span>
                            {f.signature_name ? <> · Submitted <span className="italic font-serif">{f.signature_name}</span></> : null}
                          </p>
                          <p className="mono-label text-stone-400 mt-1 text-[10px]" data-testid={`form-times-${f.id}`}>
                            {f.created_at ? <>SENT · {(f.created_at || "").slice(0, 16).replace("T", " ")}<br/></> : null}
                            {f.submitted_at ? <>COMPLETED · {(f.submitted_at || "").slice(0, 16).replace("T", " ")}<br/></> : null}
                            {f.reviewed_at ? <>REVIEWED · {(f.reviewed_at || "").slice(0, 16).replace("T", " ")} · {f.reviewed_by}</> : null}
                          </p>
                          <div className="mt-2">
                            <ConsentStatusChip status={f.status || "submitted_awaiting_review"} testid={`form-chip-${f.id}`} />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          <button data-testid={`form-view-${f.id}`} onClick={() => setOpenConsentId(f.id)}
                                  className="whitespace-nowrap flex items-center gap-2 border border-stone-900 px-3 py-2 mono-label hover:bg-stone-900 hover:text-white transition">
                            {f.status === "reviewed_signed" ? "View" : "Review"}
                          </button>
                          {f.status === "reviewed_signed" && (
                            <button data-testid={`form-pdf-${f.id}`} onClick={() => downloadForm(f.id, f.booking_reference)}
                                    className="whitespace-nowrap flex items-center gap-2 border border-stone-300 px-3 py-2 mono-label hover:bg-alt transition">
                              <Download size={12} /> PDF
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </AccordionSection>

            {/* Collapsible: Appointment History — collapsed by default */}
            <AccordionSection title="Appointment History" count={sortedBookings.length} testid="appt-history-accordion">
              <div className="mb-3 flex flex-wrap items-center gap-3 text-[10px]" data-testid="status-legend">
                <span className="mono-label text-stone-400">STATUS KEY</span>
                <LegendDot color="bg-emerald-500" label="Completed" />
                <LegendDot color="bg-yellow-400" label="Confirmed" />
                <LegendDot color="bg-amber-500" label="Pending" />
                <LegendDot color="bg-stone-400" label="No-show" />
                <LegendDot color="bg-red-500" label="Cancelled / Late" />
              </div>
              {sortedBookings.length === 0 ? (
                <p className="text-sm text-stone-500">No bookings yet.</p>
              ) : (
                <ul className="space-y-2">
                  {sortedBookings.map((b) => (
                    <li key={b.id}>
                      <button
                        onClick={() => setOpenApptId(b.id)}
                        data-testid={`appt-row-${b.id}`}
                        className="w-full text-left py-3 px-2 border-b hairline text-sm hover:bg-alt transition flex justify-between items-center gap-2"
                      >
                        <span className="min-w-0">
                          <span className="block text-stone-900">{b.date} · {b.time} — {(b.items || []).map((i) => i.name).join(", ")}</span>
                          <span className="block mono-label text-[10px] text-stone-400 mt-0.5">
                            {b.duration_min || 0} MIN · {b.payment_status || "—"}
                            {b.is_topup ? " · TOP-UP" : ""}
                            {b.is_model ? " · MODEL DAY" : ""}
                          </span>
                          {(b.items || []).some((i) => i.product_batch?.batch_number) && (
                            <span className="block text-[11px] text-stone-600 mt-1" data-testid={`history-batch-${b.id}`}>
                              {(b.items || []).filter((i) => i.product_batch?.batch_number).map((i, idx) => (
                                <span key={idx} className="block">
                                  <span className="font-medium">{i.name}</span> — {i.product_batch.product_name || "Product"} · Batch <span className="font-mono">{i.product_batch.batch_number}</span>{i.product_batch.batch_expiry ? ` · exp ${i.product_batch.batch_expiry}` : ""}
                                </span>
                              ))}
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          <StatusPill booking={b} />
                          {b.late_cancel && (
                            <span data-testid={`crm-late-chip-${b.id}`} className="mono-label inline-block px-2 py-0.5 border border-red-300 bg-red-50 text-red-700 text-[10px] whitespace-nowrap">
                              LATE · FORFEITED
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </AccordionSection>

            {/* Course Packages — surfaces every treatment course the client
                has purchased along with session progress, expiry, hold state
                and admin actions (hold / unpause / adjust / send consent
                review / mark balance paid). See spec §16. */}
            <AccordionSection
              title="Course Packages"
              count={packages.length}
              testid="client-packages-accordion"
              defaultOpen={packages.length > 0}
            >
              {packages.length === 0 ? (
                <p className="text-sm text-stone-500">
                  This client hasn't purchased any treatment courses yet.
                </p>
              ) : (
                packages.map((p) => (
                  <PackagePanel key={p.id} pkg={p} onChanged={loadPackages} />
                ))
              )}
            </AccordionSection>

            <button onClick={onClose} className="mt-8 w-full mono-label border hairline py-3 hover:bg-alt">Close</button>
          </div>
        )}
      </div>
      {openConsentId && (
        <ConsentReviewDrawer formId={openConsentId} onClose={() => setOpenConsentId(null)} onSigned={loadForms} />
      )}
      {openApptId && (
        <AppointmentDetailDrawer bookingId={openApptId} onClose={() => setOpenApptId(null)} onUpdated={loadClient} />
      )}
    </div>
  );
}


/* ── Course Package panel (spec §16) ─────────────────────────────
   Renders one client_packages row with progress, financial state,
   expiry, hold status and admin actions. Independent expandable
   card so multi-course clients scan cleanly. */
function PackagePanel({ pkg, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showHold, setShowHold] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustBalance, setAdjustBalance] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const [treatments, setTreatments] = useState([]);
  const [chosenStd, setChosenStd] = useState("");
  const [showSendReview, setShowSendReview] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState("");
  const [reviewSessionN, setReviewSessionN] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get(`/admin/packages/${pkg.id}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [pkg.id]);

  useEffect(() => {
    if (showAdjust && treatments.length === 0) {
      api.get("/treatments/all").then((r) => {
        const filtered = (r.data || []).filter((t) =>
          !((t.slug || "").includes("course-of-")) && t.active);
        setTreatments(filtered);
        if (pkg.standard_treatment_id) setChosenStd(pkg.standard_treatment_id);
      }).catch(() => setTreatments([]));
    }
  }, [showAdjust, pkg.standard_treatment_id, treatments.length]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    setBusy(false);
  };

  const putOnHold = () => run(async () => {
    if (!holdReason.trim()) { toast.error("Please provide a reason"); throw new Error("no reason"); }
    await api.post(`/admin/packages/${pkg.id}/hold`, { reason: holdReason.trim() });
    toast.success("Course placed on hold");
    setShowHold(false); setHoldReason("");
  });
  const unpause = () => run(async () => {
    const r = await api.post(`/admin/packages/${pkg.id}/unpause`);
    toast.success(`Unpaused. Expiry extended by ${r.data.paused_days_added} day(s).`);
  });
  const adjust = () => run(async () => {
    if (!chosenStd) { toast.error("Choose a standard treatment"); throw new Error(""); }
    const bal = parseFloat(adjustBalance);
    if (isNaN(bal) || bal < 0) { toast.error("Enter a valid balance"); throw new Error(""); }
    await api.post(`/admin/packages/${pkg.id}/adjust`, {
      standard_treatment_id: chosenStd, final_balance: bal, note: adjustNote,
    });
    toast.success("Course converted to standard treatment");
    setShowAdjust(false); setAdjustBalance(""); setAdjustNote("");
  });
  const sendReview = () => run(async () => {
    await api.post(`/admin/packages/${pkg.id}/send-consent-review`, {
      session_n: reviewSessionN ? parseInt(reviewSessionN, 10) : null,
      override_email: overrideEmail || null,
    });
    toast.success("Consent review email sent");
    setShowSendReview(false); setOverrideEmail(""); setReviewSessionN("");
  });
  const markPaid = () => run(async () => {
    if (!window.confirm(`Mark £${(pkg.remaining_balance || 0).toFixed(2)} in-clinic balance as paid?`)) return;
    await api.post(`/admin/packages/${pkg.id}/mark-balance-paid`, { note: "In-clinic" });
    toast.success("Balance marked paid");
  });

  const statusColour = {
    ACTIVE: "bg-emerald-50 border-emerald-200 text-emerald-700",
    ON_HOLD: "bg-amber-50 border-amber-200 text-amber-700",
    COMPLETED: "bg-stone-100 border-stone-300 text-stone-700",
    EXPIRED: "bg-red-50 border-red-200 text-red-700",
    CONVERTED: "bg-sky-50 border-sky-200 text-sky-700",
  }[pkg.status] || "bg-stone-100 border-stone-300 text-stone-700";

  const sessions = detail?.session_bookings_enriched || pkg.session_bookings || [];

  return (
    <div className="border hairline p-5 mb-4 bg-white" data-testid={`package-panel-${pkg.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-serif text-xl">{pkg.course_name}</p>
          <p className="mono-label text-[10px] text-stone-500 mt-1">
            PURCHASED · {(pkg.purchased_at || "").slice(0, 10)} · REF {pkg.id.slice(0, 8)}
          </p>
        </div>
        <span className={`mono-label text-[10px] px-2 py-1 border ${statusColour}`}
              data-testid={`package-status-${pkg.id}`}>
          {pkg.status.replace("_", " ")}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
        <SummaryPill label="TOTAL" value={gbp2(pkg.total_price)} />
        <SummaryPill label="£150 PAID" value={gbp2(pkg.booking_fee_paid)} gold />
        <SummaryPill label="BALANCE" value={gbp2(pkg.remaining_balance || 0)} />
        <SummaryPill label={pkg.balance_paid ? "BALANCE ✓" : "BALANCE STATUS"}
                     value={pkg.balance_paid ? "PAID IN CLINIC" : "DUE AT S1"} />
        <SummaryPill label="SESSIONS" value={`${pkg.sessions_completed}/${pkg.total_sessions}`} />
        <SummaryPill label="VALIDITY" value={`${pkg.validity_months} months`} />
        <SummaryPill label="EXPIRES" value={(pkg.expires_at || "").slice(0, 10)} />
        {pkg.paused_days > 0 && <SummaryPill label="PAUSED DAYS" value={pkg.paused_days} />}
      </div>

      {pkg.status === "ON_HOLD" && (
        <div className="mt-4 bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          <p className="mono-label text-[10px]">ON HOLD</p>
          <p className="mt-1">{pkg.hold_reason}</p>
          <p className="text-xs mt-1 text-amber-700">Held at · {(pkg.held_at || "").slice(0, 16).replace("T", " ")}</p>
        </div>
      )}

      {pkg.converted_to && (
        <div className="mt-4 bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900">
          <p className="mono-label text-[10px]">CONVERTED TO STANDARD TREATMENT</p>
          <p className="mt-1">{pkg.converted_to.treatment_name} · Final balance {gbp2(pkg.converted_to.final_balance)}</p>
          <p className="text-xs mt-1 text-sky-700">By {pkg.converted_to.by} · {(pkg.converted_to.converted_at || "").slice(0, 16).replace("T", " ")}</p>
        </div>
      )}

      <p className="mono-label text-[10px] text-stone-500 mt-5 mb-2">SESSIONS</p>
      <ul className="text-sm">
        {sessions.map((s) => (
          <li key={s.n} className="flex items-center justify-between py-1 border-b hairline">
            <span>
              <span className="mono-label text-stone-500 mr-2">S{s.n}</span>
              {s.date || "—"}{s.time ? ` · ${s.time}` : ""}
              {s.completed_at && <span className="text-emerald-700 ml-2 text-xs">✓ completed</span>}
              {s.cancelled_at && <span className="text-red-600 ml-2 text-xs">cancelled</span>}
              {s.consent_review && (
                <span className={`ml-2 text-xs ${s.consent_review.status === "reconfirmed" ? "text-emerald-700" : "text-amber-700"}`}>
                  · consent · {s.consent_review.status.replace(/_/g, " ")}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {(pkg.status === "ACTIVE" || pkg.status === "ON_HOLD") && (
        <div className="mt-5 flex flex-wrap gap-2">
          {pkg.status === "ACTIVE" && (
            <>
              <button data-testid={`pkg-hold-${pkg.id}`} onClick={() => setShowHold((v) => !v)} disabled={busy}
                      className="mono-label border hairline px-3 py-2 text-xs hover:bg-alt">
                {showHold ? "Cancel" : "Put on Hold"}
              </button>
              <button data-testid={`pkg-adjust-${pkg.id}`} onClick={() => setShowAdjust((v) => !v)} disabled={busy}
                      className="mono-label border hairline px-3 py-2 text-xs hover:bg-alt">
                {showAdjust ? "Cancel" : "Adjust Treatment"}
              </button>
              <button data-testid={`pkg-send-review-${pkg.id}`} onClick={() => setShowSendReview((v) => !v)} disabled={busy}
                      className="mono-label border hairline px-3 py-2 text-xs hover:bg-alt">
                {showSendReview ? "Cancel" : "Send Consent Review"}
              </button>
            </>
          )}
          {pkg.status === "ON_HOLD" && (
            <button data-testid={`pkg-unpause-${pkg.id}`} onClick={unpause} disabled={busy}
                    className="mono-label bg-stone-900 text-white px-3 py-2 text-xs">
              Unpause Course
            </button>
          )}
          {!pkg.balance_paid && (
            <button data-testid={`pkg-mark-paid-${pkg.id}`} onClick={markPaid} disabled={busy}
                    className="mono-label border hairline px-3 py-2 text-xs hover:bg-alt">
              Mark Balance Paid
            </button>
          )}
        </div>
      )}

      {showHold && (
        <div className="mt-4 border hairline p-3 bg-alt">
          <label className="mono-label text-[10px] text-stone-500">HOLD REASON</label>
          <input value={holdReason} onChange={(e) => setHoldReason(e.target.value)}
                 data-testid={`pkg-hold-reason-${pkg.id}`}
                 placeholder="e.g. Pending medical review for hypertension"
                 className="mt-2 w-full border hairline bg-white p-2 text-sm" />
          <button onClick={putOnHold} disabled={busy}
                  data-testid={`pkg-hold-confirm-${pkg.id}`}
                  className="mt-3 bg-stone-900 text-white px-4 py-2 mono-label text-xs">
            Confirm Hold
          </button>
        </div>
      )}

      {showAdjust && (
        <div className="mt-4 border hairline p-3 bg-alt">
          <label className="mono-label text-[10px] text-stone-500">CONVERT TO STANDARD TREATMENT</label>
          <select value={chosenStd} onChange={(e) => setChosenStd(e.target.value)}
                  data-testid={`pkg-adjust-treatment-${pkg.id}`}
                  className="mt-2 w-full border hairline bg-white p-2 text-sm">
            <option value="">— Choose a treatment —</option>
            {treatments.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <label className="mono-label text-[10px] text-stone-500 mt-3 block">FINAL BALANCE (£) — extra amount client pays now</label>
          <input value={adjustBalance} onChange={(e) => setAdjustBalance(e.target.value)}
                 data-testid={`pkg-adjust-balance-${pkg.id}`}
                 type="number" step="0.01" placeholder="e.g. 30.00"
                 className="mt-2 w-full border hairline bg-white p-2 text-sm" />
          <label className="mono-label text-[10px] text-stone-500 mt-3 block">NOTE (optional)</label>
          <input value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)}
                 className="mt-2 w-full border hairline bg-white p-2 text-sm" />
          <button onClick={adjust} disabled={busy}
                  data-testid={`pkg-adjust-confirm-${pkg.id}`}
                  className="mt-3 bg-stone-900 text-white px-4 py-2 mono-label text-xs">
            Confirm Adjustment
          </button>
        </div>
      )}

      {showSendReview && (
        <div className="mt-4 border hairline p-3 bg-alt">
          <label className="mono-label text-[10px] text-stone-500">SESSION NUMBER (leave blank for next upcoming)</label>
          <input value={reviewSessionN} onChange={(e) => setReviewSessionN(e.target.value)}
                 type="number" placeholder="e.g. 2"
                 className="mt-2 w-full border hairline bg-white p-2 text-sm" />
          <label className="mono-label text-[10px] text-stone-500 mt-3 block">OVERRIDE EMAIL (optional — leave blank to use client's normal email)</label>
          <input value={overrideEmail} onChange={(e) => setOverrideEmail(e.target.value)}
                 data-testid={`pkg-send-review-email-${pkg.id}`}
                 placeholder="alternate@example.com"
                 className="mt-2 w-full border hairline bg-white p-2 text-sm" />
          <button onClick={sendReview} disabled={busy}
                  data-testid={`pkg-send-review-confirm-${pkg.id}`}
                  className="mt-3 bg-stone-900 text-white px-4 py-2 mono-label text-xs">
            Send Consent Review Email
          </button>
        </div>
      )}
    </div>
  );
}


function Treatments() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [audit, setAudit] = useState([]);
  // Drag-and-drop state for intra-category reordering. ``from`` is set on
  // dragstart; ``over`` tracks the current hover row so we can tint it as
  // the drop target without reshuffling until the drop fires.
  const [drag, setDrag] = useState({ from: null, over: null });
  const load = () => api.get("/treatments/all").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (edit && !isNew && edit.id) {
      api.get(`/treatments/${edit.id}/audit`).then((r) => setAudit(r.data || [])).catch(() => setAudit([]));
    } else { setAudit([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit?.id, isNew]);
  const newTreatment = () => {
    setIsNew(true);
    setEdit({
      name: "", slug: "", category: "Anti-Wrinkle", short_description: "", full_description: "",
      duration_min: 45, price: 100, deposit_type: "fixed", deposit_value: 50,
      launch_price: false, allow_quantity: false, image_url: "",
      preparation: "", aftercare: "", important: "", contraindications: "",
      what_involves: "", consent_text: "", requires_consent: false, active: true, order: 0,
    });
  };
  const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const toggleActive = async (t) => {
    const next = !t.active;
    // Optimistic update so the admin table flips instantly.
    setRows((rs) => rs.map((r) => (r.id === t.id ? { ...r, active: next } : r)));
    try {
      await api.patch(`/treatments/${t.id}`, { active: next });
      toast.success(next
        ? `${t.name} is now visible on the client booking page`
        : `${t.name} is hidden from the client booking page`);
    } catch (e) {
      // Roll back on failure.
      setRows((rs) => rs.map((r) => (r.id === t.id ? { ...r, active: t.active } : r)));
      toast.error("Couldn't update visibility. Please try again.");
    }
  };
  const save = async () => {
    const priceNum = parseFloat(edit.price);
    const depositNum = parseFloat(edit.deposit_value);
    if (!isFinite(priceNum) || priceNum < 0) { toast.error("Price must be a valid number"); return; }
    if (!isFinite(depositNum) || depositNum < 0) { toast.error("Deposit value must be a valid number"); return; }
    // Confirm any change to money-affecting fields (price / deposit type /
    // deposit value) so a stray keystroke can never silently overwrite what
    // the admin already set. First-time creation skips the confirm.
    if (!isNew) {
      const orig = rows.find((r) => r.id === edit.id) || {};
      const priceChanged = Number(orig.price) !== priceNum;
      const depositTypeChanged = String(orig.deposit_type || "") !== String(edit.deposit_type || "");
      const depositValueChanged = Number(orig.deposit_value) !== depositNum;
      if (priceChanged || depositTypeChanged || depositValueChanged) {
        const lines = [];
        if (priceChanged) lines.push(`Price: £${Number(orig.price).toFixed(2)} → £${priceNum.toFixed(2)}`);
        if (depositTypeChanged) lines.push(`Deposit type: ${orig.deposit_type} → ${edit.deposit_type}`);
        if (depositValueChanged) lines.push(`Deposit value: £${Number(orig.deposit_value).toFixed(2)} → £${depositNum.toFixed(2)}`);
        const ok = window.confirm(
          `You're about to change ${edit.name}'s pricing:\n\n` + lines.join("\n") +
          `\n\nThis change will apply to all future bookings. Continue?`
        );
        if (!ok) return;
      }
    }
    // Whitelist: only send fields the admin actually edited. Never derive
    // requires_consent from consent_text — that field is DB-driven via the
    // consent CMS and must not be silently flipped on unrelated saves.
    const payload = {
      name: edit.name, category: edit.category,
      price: priceNum, duration_min: parseInt(edit.duration_min),
      deposit_type: edit.deposit_type, deposit_value: depositNum,
      short_description: edit.short_description, full_description: edit.full_description,
      what_involves: edit.what_involves, preparation: edit.preparation,
      aftercare: edit.aftercare, important: edit.important,
      contraindications: edit.contraindications, image_url: edit.image_url,
      consent_text: edit.consent_text || "",
      active: edit.active,
    };
    try {
      if (isNew) {
        if (!edit.name.trim()) { toast.error("Name is required"); return; }
        await api.post("/treatments", { ...payload, slug: slugify(edit.name), launch_price: false, requires_consent: !!edit.consent_text });
        toast.success("Treatment created");
      } else {
        const orig = rows.find((r) => r.id === edit.id);
        const durationChanged = !!(orig && Number(orig.duration_min) !== Number(payload.duration_min));
        await api.patch(`/treatments/${edit.id}`, payload);
        toast.success(
          durationChanged
            ? `Duration updated to ${payload.duration_min} min · now live on the client booking page`
            : "Saved",
        );
      }
      setEdit(null); setIsNew(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${edit.name}? Historical bookings will keep the original price.`)) return;
    await api.delete(`/treatments/${edit.id}`);
    toast.success("Treatment removed");
    setEdit(null); load();
  };
  return (
    <div>
      <div className="flex items-center gap-4">
        <h2 className="font-serif text-4xl font-light">Treatments</h2>
        <button data-testid="new-treatment" onClick={newTreatment} className="ml-auto border border-stone-900 px-4 py-2 mono-label hover:bg-stone-900 hover:text-white transition">+ New Treatment</button>
      </div>
      <p className="text-stone-600 text-sm mt-2">Price changes apply to future bookings only. Historical bookings retain their original price.</p>
      <div className="mt-8 space-y-6">
        {(() => {
          // Group rows by category while preserving the global order
          // (treatments arrive pre-sorted by ``order`` from the API). The
          // category sections themselves are rendered in first-seen order,
          // mirroring exactly what the client catalogue shows.
          const seen = [];
          const groups = {};
          for (const t of rows) {
            if (!groups[t.category]) {
              groups[t.category] = [];
              seen.push(t.category);
            }
            groups[t.category].push(t);
          }
          const onDragStart = (e, category, idx) => {
            setDrag({ from: { category, idx }, over: null });
            // Required for Firefox to initiate the drag session.
            try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", ""); } catch {}
          };
          const onDragOver = (e, category, idx) => {
            // Only allow drops within the same category — crossing a
            // category boundary is a no-op so treatments can never leak
            // out of their category via the drag UI.
            if (!drag.from || drag.from.category !== category) return;
            e.preventDefault();
            if (!drag.over || drag.over.idx !== idx) {
              setDrag((d) => ({ ...d, over: { category, idx } }));
            }
          };
          const onDragEnd = () => setDrag({ from: null, over: null });
          const onDrop = async (e, category, dropIdx) => {
            e.preventDefault();
            const from = drag.from;
            setDrag({ from: null, over: null });
            if (!from || from.category !== category || from.idx === dropIdx) return;
            const items = [...groups[category]];
            const [moved] = items.splice(from.idx, 1);
            items.splice(dropIdx, 0, moved);
            // Optimistic UI: splice the reordered slice back into ``rows``
            // in-place so the admin sees the new order immediately. Other
            // categories are untouched.
            const otherRows = rows.filter((r) => r.category !== category);
            setRows([...otherRows, ...items].sort((a, b) => {
              // Preserve the current grouping-by-category rendering by
              // sorting first by category appearance order, then by the
              // intra-category position we just chose.
              const aCat = seen.indexOf(a.category);
              const bCat = seen.indexOf(b.category);
              if (aCat !== bCat) return aCat - bCat;
              if (a.category === category) {
                return items.findIndex((x) => x.id === a.id) - items.findIndex((x) => x.id === b.id);
              }
              return 0;
            }));
            try {
              await api.post("/treatments/reorder", {
                category,
                ordered_ids: items.map((t) => t.id),
              });
              toast.success(`${category} order saved`);
              // Reload from the server so our local order values stay in
              // lock-step with the DB — guarantees the same order after
              // refresh, re-login and on other devices.
              load();
            } catch (err) {
              toast.error("Couldn't save new order — please try again");
              load();
            }
          };
          return seen.map((category) => (
            <div key={category} className="border hairline bg-white" data-testid={`treatment-group-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="bg-alt px-3 py-2 flex items-center justify-between border-b hairline">
                <h3 className="mono-label text-stone-700">{category}</h3>
                <span className="text-[11px] text-stone-500">Drag to reorder within this category</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-alt mono-label"><tr>
                  <th className="w-8 p-3"></th>
                  <th className="text-left p-3">Name</th>
                  <th className="text-right p-3">Duration</th>
                  <th className="text-right p-3">Price</th>
                  <th className="text-right p-3">Deposit</th>
                  <th className="text-center p-3">Status</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {groups[category].map((t, idx) => {
                    const isDragging = drag.from && drag.from.category === category && drag.from.idx === idx;
                    const isOver = drag.over && drag.over.category === category && drag.over.idx === idx && !isDragging;
                    return (
                      <tr key={t.id}
                          draggable
                          onDragStart={(e) => onDragStart(e, category, idx)}
                          onDragOver={(e) => onDragOver(e, category, idx)}
                          onDrop={(e) => onDrop(e, category, idx)}
                          onDragEnd={onDragEnd}
                          data-testid={`admin-treatment-row-${t.slug}`}
                          className={`border-t hairline select-none ${!t.active ? "bg-stone-50/50" : ""} ${isDragging ? "opacity-40" : ""} ${isOver ? "bg-amber-50" : ""}`}>
                        <td className="p-3 text-stone-400 cursor-grab active:cursor-grabbing"
                            data-testid={`drag-handle-${t.slug}`}
                            title="Drag to reorder within this category"
                            aria-label="Drag to reorder">
                          <span className="inline-flex flex-col leading-[6px] text-stone-500 text-base">⋮⋮</span>
                        </td>
                        <td className={`p-3 ${!t.active ? "opacity-50" : ""}`}>{t.name}</td>
                        <td className={`p-3 text-right ${!t.active ? "opacity-50" : ""}`}>{t.duration_min} min</td>
                        <td className={`p-3 text-right font-serif ${!t.active ? "opacity-50" : ""}`}>{gbp2(t.price)}</td>
                        <td className={`p-3 text-right ${!t.active ? "opacity-50" : ""}`}>{t.deposit_type === "fixed" ? `£${t.deposit_value}` : t.deposit_type === "percent" ? `${t.deposit_value}%` : t.deposit_type}</td>
                        <td className="p-3 text-center">
                          {t.active ? (
                            <span data-testid={`status-${t.slug}`} className="mono-label inline-block px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px] whitespace-nowrap">VISIBLE</span>
                          ) : (
                            <span data-testid={`status-${t.slug}`} className="mono-label inline-block px-2 py-1 border border-stone-300 bg-stone-100 text-stone-600 text-[10px] whitespace-nowrap">HIDDEN</span>
                          )}
                        </td>
                        <td className="p-3 whitespace-nowrap text-right">
                          <button
                            data-testid={`toggle-visibility-${t.slug}`}
                            onClick={() => toggleActive(t)}
                            className="mono-label mr-4 hover:text-stone-900"
                            title={t.active ? "Hide this treatment from the client booking page" : "Show this treatment on the client booking page"}
                          >
                            {t.active ? "Hide" : "Show"}
                          </button>
                          <button data-testid={`edit-treatment-${t.slug}`} onClick={() => { setIsNew(false); setEdit({ ...t }); }} className="mono-label hover:text-stone-900">Edit</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ));
        })()}
      </div>
      {edit && (
        <div className="fixed inset-0 z-40 bg-stone-900/30" onClick={() => { setEdit(null); setIsNew(false); }}>
          <div className="absolute right-0 top-0 h-full w-full sm:max-w-xl bg-white overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <p className="mono-label">{isNew ? "New treatment" : "Edit"}</p>
            <h3 className="font-serif text-2xl mt-2">{edit.name || "Untitled"}</h3>
            <div className="mt-6 space-y-4">
              <Input label="Name" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} />
              <div>
                <label className="mono-label">Category</label>
                <input data-testid="edit-category" value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })} list="cat-list" className="mt-2 w-full border hairline p-2 text-sm" />
                <datalist id="cat-list">
                  <option value="Anti-Wrinkle" /><option value="Dermal Fillers" /><option value="Skin Boosters" /><option value="Microneedling" />
                </datalist>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Price (£)" value={edit.price} onChange={(v) => setEdit({ ...edit, price: v })} />
                <Input label="Duration (min)" value={edit.duration_min} onChange={(v) => setEdit({ ...edit, duration_min: v })} />
              </div>
              {!isNew && (() => {
                const orig = rows.find((r) => r.id === edit.id) || {};
                const priceNum = parseFloat(edit.price);
                const depositNum = parseFloat(edit.deposit_value);
                const pending = (isFinite(priceNum) && priceNum !== Number(orig.price))
                  || (String(edit.deposit_type || "") !== String(orig.deposit_type || ""))
                  || (isFinite(depositNum) && depositNum !== Number(orig.deposit_value));
                return pending ? (
                  <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="pending-price-change">
                    <p className="mono-label text-amber-800">Unsaved pricing change</p>
                    <p className="mt-1">
                      You'll be asked to confirm on Save. Nothing changes until you click Save and confirm.
                    </p>
                  </div>
                ) : null;
              })()}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Deposit Type</label>
                  <select data-testid="edit-deposit-type" value={edit.deposit_type} onChange={(e) => setEdit({ ...edit, deposit_type: e.target.value })} className="mt-2 w-full border hairline p-2 text-sm bg-white">
                    <option value="none">None</option><option value="fixed">Fixed (£)</option><option value="percent">Percent (%)</option><option value="full">Full Payment</option>
                  </select>
                </div>
                <Input label="Deposit Value" value={edit.deposit_value} onChange={(v) => setEdit({ ...edit, deposit_value: v })} />
              </div>
              <div>
                <label className="mono-label">Treatment Image</label>
                <div className="mt-2 flex items-start gap-3">
                  {edit.image_url && (
                    <img
                      src={edit.image_url.startsWith("/api/") ? `${API}${edit.image_url.replace("/api", "")}` : edit.image_url}
                      alt=""
                      className="w-24 h-24 object-cover border hairline"
                    />
                  )}
                  <div className="flex-1 space-y-2">
                    <input
                      data-testid="edit-image-file"
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const fd = new FormData();
                        fd.append("file", f);
                        const token = localStorage.getItem("clae_token");
                        try {
                          const resp = await fetch(`${API}/uploads/image`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
                          if (!resp.ok) throw new Error("upload failed");
                          const data = await resp.json();
                          setEdit({ ...edit, image_url: data.url });
                          toast.success("Image uploaded");
                        } catch { toast.error("Upload failed"); }
                        e.target.value = "";
                      }}
                      className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:border file:border-stone-900 file:bg-white file:text-stone-900 file:mono-label file:cursor-pointer file:hover:bg-stone-900 file:hover:text-white"
                    />
                    <input
                      data-testid="edit-image"
                      value={edit.image_url || ""}
                      onChange={(e) => setEdit({ ...edit, image_url: e.target.value })}
                      placeholder="…or paste an image URL"
                      className="w-full border hairline p-2 text-xs font-mono"
                    />
                    {edit.image_url && (
                      <button
                        type="button"
                        data-testid="edit-image-clear"
                        onClick={() => setEdit({ ...edit, image_url: "" })}
                        className="mono-label text-stone-500 hover:text-red-700"
                      >Remove image</button>
                    )}
                  </div>
                </div>
              </div>
              <TxtArea label="Short Description" value={edit.short_description} onChange={(v) => setEdit({ ...edit, short_description: v })} rows={2} testid="edit-short-desc" />
              <TxtArea label="Full Description" value={edit.full_description} onChange={(v) => setEdit({ ...edit, full_description: v })} rows={3} testid="edit-full-desc" />
              <TxtArea label="What's Involved" value={edit.what_involves} onChange={(v) => setEdit({ ...edit, what_involves: v })} rows={3} testid="edit-what-involves" />
              <TxtArea label="Preparation" value={edit.preparation} onChange={(v) => setEdit({ ...edit, preparation: v })} rows={3} testid="edit-preparation" />
              <TxtArea label="Aftercare" value={edit.aftercare} onChange={(v) => setEdit({ ...edit, aftercare: v })} rows={3} testid="edit-aftercare" />
              <TxtArea label="Important" value={edit.important} onChange={(v) => setEdit({ ...edit, important: v })} rows={3} testid="edit-important" />
              <TxtArea label="Contraindications" value={edit.contraindications} onChange={(v) => setEdit({ ...edit, contraindications: v })} rows={3} testid="edit-contraindications" />
              <TxtArea label="Consent Text (shown at checkout — leave blank for none)" value={edit.consent_text || ""} onChange={(v) => setEdit({ ...edit, consent_text: v })} rows={5} testid="edit-consent-text" />

              {/* ── Product-information popup (optional) ─────────────────
                   Used by the "View Product Information" modal on the public
                   catalogue. Each sub-field maps 1:1 to a section in the
                   modal. Toggle "Information verified" on only after the
                   ingredient list has been checked against the manufacturer
                   packaging — if left off, the client-facing modal shows an
                   "Information being updated" notice instead of the body. */}
              <details className="border hairline p-4 bg-alt" data-testid="edit-product-info-section">
                <summary className="mono-label text-[11px] cursor-pointer select-none text-stone-800">
                  Product Information Popup (View Product Information)
                </summary>
                <div className="mt-3 space-y-3">
                  <TxtArea label="Overview — short, professional description shown first in the popup" value={edit.product_info?.overview || ""} onChange={(v) => setEdit({ ...edit, product_info: { ...(edit.product_info || {}), overview: v } })} rows={3} testid="edit-pi-overview" />
                  <TxtArea label="Key Ingredients — bullet-prefixed lines (e.g. • Peptides — …)" value={edit.product_info?.key_ingredients || ""} onChange={(v) => setEdit({ ...edit, product_info: { ...(edit.product_info || {}), key_ingredients: v } })} rows={5} testid="edit-pi-ingredients" />
                  <TxtArea label="Expected Benefits" value={edit.product_info?.benefits || ""} onChange={(v) => setEdit({ ...edit, product_info: { ...(edit.product_info || {}), benefits: v } })} rows={3} testid="edit-pi-benefits" />
                  <TxtArea label="Treatment Method — distinguish topical-with-microneedling vs injectable" value={edit.product_info?.treatment_method || ""} onChange={(v) => setEdit({ ...edit, product_info: { ...(edit.product_info || {}), treatment_method: v } })} rows={2} testid="edit-pi-method" />
                  <TxtArea label="Relevant Precautions" value={edit.product_info?.precautions || ""} onChange={(v) => setEdit({ ...edit, product_info: { ...(edit.product_info || {}), precautions: v } })} rows={3} testid="edit-pi-precautions" />
                  <TxtArea label="Important Product Information (disclaimer)" value={edit.product_info?.disclaimer || ""} onChange={(v) => setEdit({ ...edit, product_info: { ...(edit.product_info || {}), disclaimer: v } })} rows={2} testid="edit-pi-disclaimer" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <TxtArea label="Manufacturer / Supplier reference" value={edit.product_info?.manufacturer || ""} onChange={(v) => setEdit({ ...edit, product_info: { ...(edit.product_info || {}), manufacturer: v } })} rows={1} testid="edit-pi-manufacturer" />
                    <TxtArea label="Last verified (e.g. 2026-02-14)" value={edit.product_info?.last_verified || ""} onChange={(v) => setEdit({ ...edit, product_info: { ...(edit.product_info || {}), last_verified: v } })} rows={1} testid="edit-pi-verified-date" />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      data-testid="edit-pi-verified"
                      checked={!!edit.product_info?.verified}
                      onChange={(e) => setEdit({ ...edit, product_info: { ...(edit.product_info || {}), verified: e.target.checked } })}
                    />
                    Information verified — present ingredients as confirmed to the client
                  </label>
                  <p className="text-[11px] text-stone-500 italic">
                    Leave the checkbox off to show "Information being updated" instead of the body text.
                  </p>
                </div>
              </details>

              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={edit.active !== false} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> Active (visible on catalogue)</label>
              {!isNew && audit.length > 0 && (
                <div className="mt-4 border hairline p-3 bg-alt" data-testid="price-audit-history">
                  <p className="mono-label mb-2">Price / Deposit Change History</p>
                  <ul className="space-y-2 text-xs">
                    {audit.map((a) => (
                      <li key={a.id} className="border-l-2 border-stone-300 pl-3">
                        <div className="text-stone-500">{a.changed_at?.slice(0,19).replace('T',' ')} · {a.changed_by}</div>
                        {a.diffs.map((d, i) => (
                          <div key={i} className="text-stone-900">
                            {d.field}: <span className="line-through text-stone-500">{typeof d.from === "number" ? `£${Number(d.from).toFixed(2)}` : String(d.from)}</span>
                            {" → "}
                            <span className="font-medium">{typeof d.to === "number" ? `£${Number(d.to).toFixed(2)}` : String(d.to)}</span>
                          </div>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="mt-6 flex gap-3">
              <button data-testid="save-treatment" onClick={save} className="flex-1 bg-stone-900 text-white py-3 mono-label" style={{ color: "#fff" }}>{isNew ? "Create" : "Save"}</button>
              {!isNew && <button data-testid="delete-treatment" onClick={remove} className="border border-red-700 text-red-700 px-4 py-3 mono-label hover:bg-red-700 hover:text-white transition">Remove</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AvailabilityTab() {
  const [days, setDays] = useState([]);
  const load = () => api.get("/availability").then((r) => setDays(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { await api.put("/availability", days); toast.success("Saved"); load(); };
  const setDay = (idx, patch) => setDays(days.map((d, i) => i === idx ? { ...d, ...patch } : d));
  const names = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div>
      <h2 className="font-serif text-4xl font-light">Availability</h2>
      <div className="mt-8 border hairline bg-white divide-y">
        <div className="hidden sm:grid px-4 py-3 grid-cols-6 gap-3 mono-label text-[10px] text-stone-500" data-testid="availability-headings">
          <span>Day</span><span></span><span>Open</span><span>Close</span><span>Break start</span><span>Break end</span>
        </div>
        {days.map((d, i) => (
          <div key={i} className="p-4 grid grid-cols-2 sm:grid-cols-6 gap-3 items-center">
            <span className="mono-label">{names[d.weekday]}</span>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!d.closed} onChange={(e) => setDay(i, { closed: !e.target.checked })} /> Open</label>
            <input data-testid={`open-${i}`} type="time" value={d.open} onChange={(e) => setDay(i, { open: e.target.value })} className="border hairline p-2 text-sm" />
            <input data-testid={`close-${i}`} type="time" value={d.close} onChange={(e) => setDay(i, { close: e.target.value })} className="border hairline p-2 text-sm" />
            <input placeholder="Break start" type="time" value={d.break_start || ""} onChange={(e) => setDay(i, { break_start: e.target.value })} className="border hairline p-2 text-sm" />
            <input placeholder="Break end" type="time" value={d.break_end || ""} onChange={(e) => setDay(i, { break_end: e.target.value })} className="border hairline p-2 text-sm" />
          </div>
        ))}
      </div>
      <button data-testid="save-availability" onClick={save} className="mt-6 bg-stone-900 text-white px-8 py-3 mono-label" style={{ color: "#fff" }}>Save</button>

      <BlockedDates />
      <TimeBlocks />
    </div>
  );
}


/* Block Out Time — admin can block specific hours within a working day
   (e.g. 2pm–4pm doctor's appointment). The /slots endpoint filters any
   candidate slot that overlaps a block, so client-facing availability
   updates the instant Admin saves. NOT an appointment — never creates
   clients, bookings, payments, emails, consent forms, aftercare, loyalty
   stamps, referral credits or stock deductions. */
function TimeBlocks() {
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // null | row | { new: true }
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/admin/time-blocks").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ date: todayStr, start_time: "14:00", end_time: "16:00", reason: "", notes: "", new: true });
  const startEdit = (row) => setEditing({ ...row });

  const save = async () => {
    if (!editing) return;
    if (!editing.date || !editing.start_time || !editing.end_time) {
      toast.error("Date, start and end are required"); return;
    }
    if (editing.end_time <= editing.start_time) {
      toast.error("End time must be after start time"); return;
    }
    setBusy(true);
    try {
      const payload = {
        date: editing.date, start_time: editing.start_time, end_time: editing.end_time,
        reason: (editing.reason || "").trim(), notes: (editing.notes || "").trim(),
      };
      if (editing.new) {
        await api.post("/admin/time-blocks", payload);
        toast.success("Time blocked");
      } else {
        await api.patch(`/admin/time-blocks/${editing.id}`, payload);
        toast.success("Updated");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
    setBusy(false);
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove block on ${row.date} ${row.start_time}–${row.end_time}?`)) return;
    try {
      await api.delete(`/admin/time-blocks/${row.id}`);
      toast.success("Block removed — those hours are available again");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  // Future-first list.
  const sorted = [...rows]
    .filter((r) => r.date >= todayStr)
    .sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));

  return (
    <section className="mt-14" data-testid="time-blocks-section">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-2xl font-light">Block Out Time</h3>
          <p className="text-sm text-stone-500 mt-1">
            Block specific hours within a working day (e.g. 2pm–4pm). Clients
            cannot book any treatment that overlaps a block. Working-hours,
            treatment duration and the 15-minute gap still apply.
          </p>
        </div>
        <button data-testid="timeblock-add-btn" onClick={startNew}
                className="bg-stone-900 text-white py-2 px-4 mono-label hover:bg-[#C5A059] transition" style={{ color: "#fff" }}>
          + Block Out Time
        </button>
      </div>

      <div className="mt-4">
        {sorted.length === 0 ? (
          <p className="text-sm text-stone-500">No upcoming block-out times.</p>
        ) : (
          <ul className="border hairline bg-white divide-y" data-testid="time-blocks-list">
            {sorted.map((r) => (
              <li key={r.id} className="p-4 flex items-center justify-between gap-3" data-testid={`timeblock-row-${r.id}`}>
                <div>
                  <p className="text-sm text-stone-900">
                    {r.date} · <span className="tabular-nums">{r.start_time}–{r.end_time}</span>
                    {r.reason && <span className="text-stone-600"> · {r.reason}</span>}
                  </p>
                  {r.notes && <p className="text-xs text-stone-500 mt-1">{r.notes}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`timeblock-edit-${r.id}`} onClick={() => startEdit(r)}
                          className="mono-label text-[11px] border border-stone-900 px-3 py-1 hover:bg-stone-900 hover:text-white transition">Edit</button>
                  <button data-testid={`timeblock-delete-${r.id}`} onClick={() => remove(r)}
                          className="mono-label text-[11px] border border-red-700 text-red-700 px-3 py-1 hover:bg-red-700 hover:text-white transition">Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
             onClick={() => setEditing(null)} data-testid="timeblock-editor-modal">
          <div className="bg-white border hairline w-full max-w-xl mt-10 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-stone-900">{editing.new ? "Block Out Time" : "Edit block"}</h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block">
                <span className="mono-label text-[10px] text-stone-500 block mb-1">DATE</span>
                <input data-testid="timeblock-field-date" type="date" value={editing.date} min={todayStr}
                       onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                       className="border hairline px-2 py-2 text-sm w-full focus:outline-none focus:border-stone-900" />
              </label>
              <label className="block">
                <span className="mono-label text-[10px] text-stone-500 block mb-1">START</span>
                <input data-testid="timeblock-field-start" type="time" value={editing.start_time}
                       onChange={(e) => setEditing({ ...editing, start_time: e.target.value })}
                       className="border hairline px-2 py-2 text-sm w-full focus:outline-none focus:border-stone-900" />
              </label>
              <label className="block">
                <span className="mono-label text-[10px] text-stone-500 block mb-1">END</span>
                <input data-testid="timeblock-field-end" type="time" value={editing.end_time}
                       onChange={(e) => setEditing({ ...editing, end_time: e.target.value })}
                       className="border hairline px-2 py-2 text-sm w-full focus:outline-none focus:border-stone-900" />
              </label>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3">
              <label className="block">
                <span className="mono-label text-[10px] text-stone-500 block mb-1">REASON</span>
                <input data-testid="timeblock-field-reason" value={editing.reason}
                       onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
                       placeholder="e.g. Doctors Appointment"
                       className="border hairline px-2 py-2 text-sm w-full focus:outline-none focus:border-stone-900" />
              </label>
              <label className="block">
                <span className="mono-label text-[10px] text-stone-500 block mb-1">NOTES (OPTIONAL)</span>
                <textarea data-testid="timeblock-field-notes" value={editing.notes || ""} rows={2}
                       onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                       className="border hairline px-2 py-2 text-sm w-full focus:outline-none focus:border-stone-900" />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button onClick={() => setEditing(null)}
                      className="mono-label text-xs border border-stone-400 text-stone-700 px-4 py-2 hover:bg-stone-100">Cancel</button>
              <button data-testid="timeblock-save-btn" onClick={save} disabled={busy}
                      className="mono-label text-xs border border-stone-900 bg-stone-900 text-white px-4 py-2 hover:bg-stone-700 disabled:opacity-50 transition">
                {busy ? "Saving…" : (editing.new ? "Save block" : "Save changes")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


/* Blocked Dates / Holidays — admin can book out single dates or ranges
   (e.g. holidays, clinic closures). Lives inside the Availability tab.
   The /slots endpoint already filters by blocked_dates, so clients
   cannot book any treatment (including course sessions) on blocked days. */
function BlockedDates() {
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const [rows, setRows] = useState([]);
  const [start, setStart] = useState(todayStr);
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/blocked-dates").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const addBlock = async () => {
    if (!start) { toast.error("Choose a start date"); return; }
    const useEnd = end && end >= start ? end : start;
    setBusy(true);
    try {
      const r = await api.post("/blocked-dates/range", {
        start_date: start, end_date: useEnd, reason: reason.trim(),
      });
      toast.success(`Blocked ${r.data.days_blocked} day${r.data.days_blocked === 1 ? "" : "s"}`);
      setStart(todayStr); setEnd(""); setReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not block dates");
    }
    setBusy(false);
  };

  const removeBlock = async (bid) => {
    if (!window.confirm("Remove this blocked date?")) return;
    await api.delete(`/blocked-dates/${bid}`);
    toast.success("Blocked date removed");
    load();
  };

  // Future only, sorted ascending. Group consecutive same-reason days into
  // ranges for cleaner display, but keep per-day delete handles so partial
  // removal still works.
  const future = rows.filter((r) => r.date >= todayStr).sort((a, b) => a.date.localeCompare(b.date));
  const groups = [];
  for (const r of future) {
    const last = groups[groups.length - 1];
    const nextDay = last ? format(new Date(new Date(last.end.date + "T00:00:00").getTime() + 86400000), "yyyy-MM-dd") : null;
    if (last && (last.end.reason || "") === (r.reason || "") && r.date === nextDay) {
      last.items.push(r); last.end = r;
    } else {
      groups.push({ start: r, end: r, items: [r] });
    }
  }

  return (
    <section className="mt-14" data-testid="blocked-dates-section">
      <h3 className="font-serif text-2xl font-light">Blocked Dates / Holidays</h3>
      <p className="text-sm text-stone-500 mt-1">
        Book out specific dates or ranges when the clinic is closed. Blocked days
        are hidden from the client booking calendar for every treatment, including
        course sessions.
      </p>

      <div className="mt-6 border hairline bg-white p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <div>
          <label className="mono-label text-[10px] text-stone-500">FROM</label>
          <input data-testid="block-start" type="date" value={start} min={todayStr}
                 onChange={(e) => setStart(e.target.value)}
                 className="mt-1 w-full border hairline p-2 text-sm" />
        </div>
        <div>
          <label className="mono-label text-[10px] text-stone-500">TO (OPTIONAL)</label>
          <input data-testid="block-end" type="date" value={end} min={start || todayStr}
                 onChange={(e) => setEnd(e.target.value)}
                 placeholder="Same as From"
                 className="mt-1 w-full border hairline p-2 text-sm" />
        </div>
        <div>
          <label className="mono-label text-[10px] text-stone-500">REASON</label>
          <input data-testid="block-reason" type="text" value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 placeholder="e.g. Holiday"
                 className="mt-1 w-full border hairline p-2 text-sm" />
        </div>
        <button data-testid="block-add" onClick={addBlock} disabled={busy}
                className="bg-stone-900 text-white py-2 mono-label disabled:opacity-40"
                style={{ color: "#fff" }}>
          {busy ? "Blocking…" : "Block Dates"}
        </button>
      </div>

      <div className="mt-6">
        <p className="mono-label text-[10px] text-stone-500 mb-2">UPCOMING BLOCKED DATES</p>
        {groups.length === 0 ? (
          <p className="text-sm text-stone-500">No upcoming blocked dates.</p>
        ) : (
          <ul className="border hairline bg-white divide-y" data-testid="blocked-dates-list">
            {groups.map((g, i) => {
              const label = g.start.date === g.end.date
                ? g.start.date
                : `${g.start.date} → ${g.end.date} (${g.items.length} days)`;
              return (
                <li key={i} className="p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-stone-900">{label}</p>
                    {g.start.reason && <p className="text-xs text-stone-500 mt-1">{g.start.reason}</p>}
                  </div>
                  <div className="flex gap-2">
                    {g.items.map((it) => (
                      <button key={it.id}
                              data-testid={`block-remove-${it.date}`}
                              onClick={() => removeBlock(it.id)}
                              title={`Remove ${it.date}`}
                              className="mono-label text-[10px] border hairline px-2 py-1 hover:bg-alt">
                        Remove {it.items === 1 ? "" : it.date.slice(5)}
                      </button>
                    )).slice(0, 1)}
                    {g.items.length > 1 && (
                      <button data-testid={`block-remove-range-${g.start.date}`}
                              onClick={async () => {
                                if (!window.confirm(`Remove all ${g.items.length} blocked days?`)) return;
                                for (const it of g.items) {
                                  await api.delete(`/blocked-dates/${it.id}`);
                                }
                                toast.success(`Removed ${g.items.length} days`);
                                load();
                              }}
                              className="mono-label text-[10px] border border-stone-900 px-2 py-1 hover:bg-stone-900 hover:text-white">
                        Remove Range
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function Reports() {
  const [from, setFrom] = useState(format(new Date(new Date().setDate(1)), "yyyy-MM-dd"));
  const [to, setTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [data, setData] = useState(null);
  const [showAllTreatments, setShowAllTreatments] = useState(false);
  const load = () => api.get("/reports", { params: { from_date: from, to_date: to } }).then((r) => setData(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // Full treatment breakdown from revenue_by_treatment (for the "View all" toggle).
  const allTreatments = React.useMemo(() => {
    if (!data) return [];
    const counts = Object.fromEntries(data.popular_treatments || []);
    const rows = Object.entries(data.revenue_by_treatment || {}).map(([name, revenue]) => ({
      name, revenue: Number(revenue) || 0, count: counts[name] || 0,
    }));
    // Fill in counts for treatments only present in popular_treatments (unlikely
    // but safe): the two dicts share keys, so this is idempotent.
    return rows.sort((a, b) => b.count - a.count || b.revenue - a.revenue);
  }, [data]);
  const needsAttentionEmpty = data &&
    !data.outstanding_count && !data.cancelled && !data.no_show;
  return (
    <div>
      <div className="flex items-center gap-4 flex-wrap">
        <h2 className="font-serif text-4xl font-light">Reports</h2>
        <input data-testid="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="ml-auto border hairline p-2 text-sm" />
        <input data-testid="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border hairline p-2 text-sm" />
        <button data-testid="report-run" onClick={load} className="border border-stone-900 px-4 py-2 mono-label hover:bg-stone-900 hover:text-white transition">Run</button>
      </div>
      {data && (
        <>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Revenue" value={gbp2(data.total_revenue)} />
            <Stat label="Deposits" value={gbp2(data.deposits_collected)} />
            <Stat label="Outstanding" value={gbp2(data.outstanding_balances)} />
            <Stat label="Avg Order" value={gbp2(data.average_order_value)} />
            <Stat label="Bookings" value={data.bookings_total} />
            <Stat label="Completed" value={data.completed} />
            <Stat label="Cancelled" value={data.cancelled} />
            <Stat label="No-Shows" value={data.no_show} />
          </div>

          {/* ── UPCOMING ─────────────────────────────────────────────── */}
          <h3 className="font-serif text-2xl mt-12 mb-4" data-testid="reports-upcoming-heading">Upcoming</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="reports-upcoming">
            <Stat label="Next 7 Days" value={data.upcoming_7_count} />
            <Stat label="Next 30 Days" value={data.upcoming_30_count} />
            <Stat label="Upcoming Booking Value" value={gbp2(data.upcoming_30_value)} />
          </div>

          {/* ── NEEDS ATTENTION ─────────────────────────────────────── */}
          <h3 className="font-serif text-2xl mt-12 mb-4" data-testid="reports-attention-heading">Needs Attention</h3>
          {needsAttentionEmpty ? (
            <div className="border hairline bg-white p-8 text-center" data-testid="reports-attention-empty">
              <p className="mono-label text-stone-400 text-[10px]">ALL CLEAR</p>
              <p className="font-serif text-xl text-stone-800 mt-1">Nothing requires attention</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="reports-attention">
              {data.outstanding_count > 0 && (
                <Stat
                  label="Outstanding Balances"
                  value={data.outstanding_count}
                  sublabel={`${gbp2(data.outstanding_amount)} across ${data.outstanding_count} booking${data.outstanding_count === 1 ? "" : "s"}`}
                />
              )}
              {data.cancelled > 0 && (
                <Stat
                  label="Cancellations"
                  value={data.cancelled}
                  sublabel="in selected period"
                />
              )}
              {data.no_show > 0 && (
                <Stat
                  label="No-Shows"
                  value={data.no_show}
                  sublabel="in selected period"
                />
              )}
            </div>
          )}

          {/* ── TOP TREATMENTS ──────────────────────────────────────── */}
          <div className="mt-12 mb-4 flex items-baseline justify-between gap-4 flex-wrap">
            <h3 className="font-serif text-2xl" data-testid="reports-top-treatments-heading">Top Treatments</h3>
            {allTreatments.length > 5 && (
              <button
                onClick={() => setShowAllTreatments((v) => !v)}
                data-testid="reports-view-all-treatments"
                className="mono-label text-[10px] text-stone-500 hover:text-stone-900"
              >{showAllTreatments ? "SHOW TOP 5" : `VIEW ALL (${allTreatments.length})`}</button>
            )}
          </div>
          <div className="border hairline bg-white" data-testid="reports-top-treatments">
            {(showAllTreatments ? allTreatments : (data.top_treatments || [])).length === 0 ? (
              <p className="p-4 text-stone-500 text-sm">No data.</p>
            ) : (
              (showAllTreatments ? allTreatments : data.top_treatments).map((t) => (
                <div key={t.name} className="flex items-center justify-between gap-4 p-3 border-t first:border-t-0 hairline">
                  <span className="text-sm text-stone-800">{t.name}</span>
                  <div className="flex items-center gap-4">
                    <span className="mono-label text-[10px] text-stone-500">{t.count} bookings</span>
                    <span className="mono-label text-stone-900">{gbp2(t.revenue)}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── CLIENTS ─────────────────────────────────────────────── */}
          <h3 className="font-serif text-2xl mt-12 mb-4" data-testid="reports-clients-heading">Clients</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="reports-clients">
            <Stat label="New Clients" value={data.new_clients_count} />
            <Stat label="Returning Clients" value={data.returning_clients_count} />
            <Stat label="New-Client Revenue" value={gbp2(data.new_clients_revenue)} />
            <Stat label="Returning-Client Revenue" value={gbp2(data.returning_clients_revenue)} />
          </div>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value, sublabel }) => (
  <div className="border hairline bg-white p-5">
    <p className="mono-label">{label}</p>
    <p className="mt-2 font-serif text-3xl text-stone-900">{value}</p>
    {sublabel && <p className="mt-1 text-[11px] text-stone-500">{sublabel}</p>}
  </div>
);

function SettingsTab() {
  const formatHour12 = (hhmm) => {
    if (!hhmm) return "";
    const [h, m] = hhmm.split(":").map(Number);
    if (Number.isNaN(h)) return "";
    const mm = Number(m) || 0;
    const period = h >= 12 ? "pm" : "am";
    const h12 = h % 12 || 12;
    return mm === 0 ? `${h12}${period}` : `${h12}:${String(mm).padStart(2, "0")}${period}`;
  };
  const [google_review_url, setUrl] = useState("");
  const [clinic_address, setAddr] = useState("");
  const [cancellation_policy, setPolicy] = useState("");
  const [referral_amount_gbp, setReferral] = useState(20);
  const [buffer_min, setBuffer] = useState(15);
  const [whatsapp_link, setWhatsLink] = useState("");
  const [whatsapp_enabled, setWhatsEnabled] = useState(false);
  const [whatsapp_prefill_message, setWhatsMsg] = useState("");
  const [whatsapp_hours_enabled, setWhatsHoursEnabled] = useState(false);
  const [whatsapp_open, setWhatsOpen] = useState("10:00");
  const [whatsapp_close, setWhatsClose] = useState("18:00");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/settings").then((r) => {
      setUrl(r.data.google_review_url || "");
      setAddr(r.data.clinic_address || "");
      setPolicy(r.data.cancellation_policy || "");
      setReferral(r.data.referral_amount_gbp ?? 20);
      setBuffer(r.data.buffer_min ?? 15);
      setWhatsLink(r.data.whatsapp_link || "");
      setWhatsEnabled(!!r.data.whatsapp_enabled);
      setWhatsMsg(r.data.whatsapp_prefill_message || "");
      setWhatsHoursEnabled(!!r.data.whatsapp_hours_enabled);
      setWhatsOpen(r.data.whatsapp_open || "10:00");
      setWhatsClose(r.data.whatsapp_close || "18:00");
      setLoading(false);
    });
  }, []);
  const save = async () => {
    const amount = Number(referral_amount_gbp);
    const buf = Number(buffer_min);
    if (Number.isNaN(amount) || amount < 0) { toast.error("Referral amount must be a positive number"); return; }
    if (!Number.isInteger(buf) || buf < 0 || buf > 120) { toast.error("Buffer must be between 0 and 120 minutes"); return; }
    const hhmm = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (whatsapp_hours_enabled && (!hhmm.test(whatsapp_open) || !hhmm.test(whatsapp_close))) {
      toast.error("Opening hours must be in HH:MM 24-hour format"); return;
    }
    if (whatsapp_hours_enabled && whatsapp_open >= whatsapp_close) {
      toast.error("Close time must be after open time"); return;
    }
    await api.put("/settings", {
      google_review_url, clinic_address, cancellation_policy,
      referral_amount_gbp: amount, buffer_min: buf,
      whatsapp_link: whatsapp_link.trim(),
      whatsapp_enabled,
      whatsapp_prefill_message: whatsapp_prefill_message.trim(),
      whatsapp_hours_enabled,
      whatsapp_open,
      whatsapp_close,
    });
    toast.success("Settings saved — changes are live");
  };
  if (loading) return <p className="mono-label">Loading…</p>;
  return (
    <div>
      <h2 className="font-serif text-4xl font-light">Settings</h2>
      <p className="text-stone-600 text-sm mt-2">Configuration used across the client site and automated emails.</p>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-3xl">
        <div>
          <label className="mono-label">Buffer Between Appointments (minutes)</label>
          <input
            data-testid="settings-buffer-min"
            type="number"
            min="0"
            max="120"
            step="5"
            value={buffer_min}
            onChange={(e) => setBuffer(e.target.value)}
            className="mt-2 w-full border hairline p-3 text-sm"
          />
          <p className="mt-2 text-xs text-stone-500">Time reserved after every appointment for cleaning, admin and set-up. Applied instantly to online booking, admin move flows and available-slot calculations. Default 15 minutes.</p>
        </div>
        <div>
          <label className="mono-label">Referral Promotion Amount (£)</label>
          <input
            data-testid="settings-referral-amount"
            type="number"
            min="0"
            step="1"
            value={referral_amount_gbp}
            onChange={(e) => setReferral(e.target.value)}
            className="mt-2 w-full border hairline p-3 text-sm"
          />
          <p className="mt-2 text-xs text-stone-500">Applied instantly to new referral checkouts, the "Share the Glow" success card, and every referral email. Changes take effect on the next request — no restart needed. Historical referral credits are never re-calculated.</p>
        </div>
        <div>
          <label className="mono-label">Google Review URL</label>
          <input data-testid="settings-review-url" value={google_review_url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://g.page/r/…/review" className="mt-2 w-full border hairline p-3 text-sm" />
          <p className="mt-2 text-xs text-stone-500">Included in the "thank you" email sent when an appointment is marked Completed.</p>
        </div>
        <div>
          <label className="mono-label">Clinic Address</label>
          <input data-testid="settings-address" value={clinic_address} onChange={(e) => setAddr(e.target.value)}
            className="mt-2 w-full border hairline p-3 text-sm" />
          <p className="mt-2 text-xs text-stone-500">Shown on the booking confirmation email footer.</p>
        </div>
        <div className="lg:col-span-2">
          <label className="mono-label">Cancellation Policy (Terms & Conditions)</label>
          <textarea data-testid="settings-cancellation" value={cancellation_policy} onChange={(e) => setPolicy(e.target.value)}
            rows={6} className="mt-2 w-full border hairline p-3 text-sm" />
          <p className="mt-2 text-xs text-stone-500">Shown at checkout as a required T&Cs acceptance and printed on every booking confirmation email.</p>
        </div>
        {/* ── WhatsApp floating button ─────────────────────────────── */}
        <div className="lg:col-span-2 border-t hairline pt-6 mt-2">
          <p className="mono-label text-stone-900">WhatsApp Business Chat</p>
          <p className="mt-1 text-xs text-stone-500">
            Shows a floating WhatsApp button in the bottom-right corner of every public page.
            Clicking it opens the WhatsApp app on mobile and WhatsApp Web on desktop. Hidden
            inside the admin portal.
          </p>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-start">
            <div>
              <label className="mono-label text-[11px]">WhatsApp Business Link</label>
              <input
                data-testid="settings-whatsapp-link"
                value={whatsapp_link}
                onChange={(e) => setWhatsLink(e.target.value)}
                placeholder="e.g. https://wa.me/447123456789  or  +44 7123 456789"
                className="mt-2 w-full border hairline p-3 text-sm"
              />
              <p className="mt-2 text-xs text-stone-500">
                Accepts a full <code>https://wa.me/…</code> link, a WhatsApp Business short link,
                or a phone number (with country code) — we'll convert it automatically.
              </p>
            </div>
            <label className="inline-flex items-center gap-3 border hairline bg-white px-4 py-3 cursor-pointer select-none mt-6 lg:mt-7"
                   data-testid="settings-whatsapp-toggle-label">
              <input
                data-testid="settings-whatsapp-enabled"
                type="checkbox"
                checked={whatsapp_enabled}
                onChange={(e) => setWhatsEnabled(e.target.checked)}
                className="h-4 w-4 accent-[#25D366]"
              />
              <span className="mono-label text-[11px]">
                {whatsapp_enabled ? "ENABLED" : "DISABLED"}
              </span>
            </label>
          </div>

          {/* Pre-fill message */}
          <div className="mt-6">
            <label className="mono-label text-[11px]">Pre-fill Message</label>
            <textarea
              data-testid="settings-whatsapp-prefill"
              value={whatsapp_prefill_message}
              onChange={(e) => setWhatsMsg(e.target.value)}
              rows={2}
              maxLength={280}
              placeholder="Hi Claudia, I'd love to book…"
              className="mt-2 w-full border hairline p-3 text-sm"
            />
            <p className="mt-2 text-xs text-stone-500">
              Appears pre-typed in the client's WhatsApp when they tap the button —
              so the chat opens warm and ready to reply. Leave empty for a blank chat.
            </p>
          </div>

          {/* Opening-hours gate */}
          <div className="mt-6 border hairline bg-[#FBF8F3] p-4">
            <label className="inline-flex items-center gap-3 cursor-pointer select-none"
                   data-testid="settings-whatsapp-hours-label">
              <input
                data-testid="settings-whatsapp-hours-enabled"
                type="checkbox"
                checked={whatsapp_hours_enabled}
                onChange={(e) => setWhatsHoursEnabled(e.target.checked)}
                className="h-4 w-4 accent-[#C5A059]"
              />
              <span className="mono-label text-[11px]">
                DIM OUTSIDE OPENING HOURS {whatsapp_hours_enabled ? "· ON" : "· OFF"}
              </span>
            </label>
            <p className="mt-2 text-xs text-stone-500">
              Outside these hours (London time) the button stays visible but dims
              and shows a small "replies by {whatsapp_hours_enabled ? formatHour12(whatsapp_open) : "10am"}" note so clients know when to expect a reply.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-4 max-w-sm">
              <div>
                <label className="mono-label text-[10px]">OPEN</label>
                <input
                  data-testid="settings-whatsapp-open"
                  type="time"
                  value={whatsapp_open}
                  disabled={!whatsapp_hours_enabled}
                  onChange={(e) => setWhatsOpen(e.target.value)}
                  className="mt-1 w-full border hairline p-2 text-sm bg-white disabled:bg-stone-100 disabled:text-stone-400"
                />
              </div>
              <div>
                <label className="mono-label text-[10px]">CLOSE</label>
                <input
                  data-testid="settings-whatsapp-close"
                  type="time"
                  value={whatsapp_close}
                  disabled={!whatsapp_hours_enabled}
                  onChange={(e) => setWhatsClose(e.target.value)}
                  className="mt-1 w-full border hairline p-2 text-sm bg-white disabled:bg-stone-100 disabled:text-stone-400"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <button data-testid="settings-save" onClick={save} className="mt-6 bg-stone-900 text-white px-8 py-3 mono-label" style={{ color: "#fff" }}>Save</button>
    </div>
  );
}
const Info = ({ label, value }) => (
  <div><p className="mono-label">{label}</p><p className="mt-1 font-serif text-lg">{value}</p></div>
);
const Badge = ({ children, tone }) => (
  <span className={`mono-label inline-block px-2 py-1 border hairline ${tone === "danger" ? "bg-red-50 text-red-700 border-red-300" : "bg-alt"}`}>{children}</span>
);

function statusTone(b) {
  const s = b.appointment_status;
  if (s === "Completed") return { dot: "bg-emerald-500", text: "text-emerald-800" };
  if (s === "Cancelled") return { dot: "bg-red-500",     text: "text-red-700"     };
  if (s === "No-Show")   return { dot: "bg-stone-400",   text: "text-stone-500"   };
  if (s === "Confirmed") return { dot: "bg-yellow-500",  text: "text-yellow-800"  };
  // Pending / anything else → amber
  return { dot: "bg-amber-500", text: "text-amber-800" };
}

const StatusPill = ({ booking }) => {
  const t = statusTone(booking);
  const pending = booking.pending_appointment_status;
  // Compact dot + tinted uppercase text — no more heavy chip outline. Kept
  // as a <span> with the same test-id so admin flows continue to find it.
  return (
    <span
      data-testid={`status-pill-${booking.id}`}
      className={`mono-label inline-flex items-center gap-1.5 text-[11px] whitespace-nowrap ${t.text}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${t.dot}`} />
      {booking.appointment_status.toUpperCase()}
      {pending && pending !== booking.appointment_status && (
        <span data-testid={`status-pending-${booking.id}`}
              className="ml-1 inline-flex items-center gap-1 text-[10px] bg-amber-50 border border-amber-200 text-amber-800 px-1.5 py-[1px] rounded-sm">
          <span className="inline-block w-1 h-1 rounded-full bg-amber-500 animate-pulse" />
          PENDING · {pending.toUpperCase()}
        </span>
      )}
    </span>
  );
};

/**
 * Small, secondary consent indicator that lives directly under the primary
 * appointment status. Purposefully text-only (icon + label, muted colour) so
 * it never competes visually with the appointment status. Same operational
 * meaning as the old bordered chips.
 */
function ConsentSubchip({ booking: b }) {
  if (["Cancelled", "No-Show"].includes(b.appointment_status)) return null;
  // Consultations & top-ups reuse other consent flows — never chase for a
  // new form on these rows.
  if (b.is_consultation || b.is_topup) return null;
  let icon, label, cls, title;
  if (b.consent_forms_completed) {
    icon = "✓"; label = "Consent complete"; cls = "text-emerald-700";
    title = "Practitioner has reviewed & signed all consents";
  } else if (b.consent_forms_submitted) {
    icon = "◐"; label = "Awaiting review"; cls = "text-amber-700";
    title = "Client submitted — awaiting practitioner review";
  } else if (b.consent_reminder_sent_at) {
    icon = "↗"; label = "Reminder sent"; cls = "text-amber-700";
    title = `Reminder sent ${b.consent_reminder_sent_at}`;
  } else {
    icon = "⚠"; label = "Consent pending"; cls = "text-red-600";
    title = "Consent still outstanding";
  }
  return (
    <span
      data-testid={`consent-chip-${b.reference}`}
      title={title}
      className={`inline-flex items-center gap-1 text-[10px] ${cls}`}
    >
      <span aria-hidden="true">{icon}</span>
      <span>{label}</span>
    </span>
  );
}

const LegendDot = ({ color, label }) => (
  <span className="inline-flex items-center gap-1.5 text-stone-500">
    <span className={`inline-block w-2 h-2 rounded-full ${color}`} />
    {label}
  </span>
);
const Input = ({ label, value, onChange }) => (
  <div><label className="mono-label">{label}</label><input value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full border hairline p-2 text-sm" /></div>
);
const TxtArea = ({ label, value, onChange, rows = 3, testid }) => (
  <div>
    <label className="mono-label">{label}</label>
    <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="mt-2 w-full border hairline p-2 text-sm leading-relaxed" />
  </div>
);
